(function () {
  'use strict';

  var API_URL = (window.QUOTE_API_URL || '').replace(/\/$/, '');

  var container = document.getElementById('custom-quote-app');
  if (!container) return;

  var params = new URLSearchParams(window.location.search);
  var quoteId = params.get('id');

  injectStyles();

  // ── Form state ──────────────────────────────────────────────────────────────

  var cqStep = 1;
  var cqData = {};
  // Which steps this customer actually landed on. Steps 2 and 3 are each conditionally
  // skippable, so the trail above the current step has to reflect the path taken, not the
  // full four. Entries past the current step are dropped on every jump back, since a
  // changed answer can change which steps come next.
  var cqVisited = {};
  // group id → the sku whose sole-option auto-selection has already been applied. Keyed by
  // sku, not a bare flag, so changing print style (which can change which single option a
  // group has left) re-applies, while a deliberate deselect of the same option stays
  // deselected instead of being silently re-added on the next render.
  var cqSoleApplied = {};

  // Product group state
  var cqGroups = null;
  var cqSettings = null;
  var cqActiveCat = 'popular';
  var cqSelectedGroups = {}; // id → group object
  var cqGroupOptions = {}; // id → array of {sku, decoration_method, color, color_image_url, size, image_url}, fetched lazily on step 4

  var CATEGORY_LABELS = {
    popular:  'Most Popular',
    classic:  'Classic Styles',
    stainless: 'Stainless Steel',
    handles:  'Handle Tumblers',
    hydro:    'Hydro Bottles',
    speakers: 'Speaker Tumblers',
    wine:     'Wine & Liquor',
    shots:    'Mini Tumblers',
    coffee:   'Coffee & Mugs',
    glass:    'Glass Cans',
    cancoolers: 'Can Coolers',
    kids:     'Kids',
    all:      'All',
  };

  var CAT_EMOJI = {
    stainless: '🥤', handles: '🫙', hydro: '💧', glass: '🔮',
    speakers: '🔊', cancoolers: '🧊', wine: '🍷', coffee: '☕',
    shots: '🥂', kids: '🧸',
  };

  // Deferred until after the state above is initialized (2026-08-20) — step 1 is now the
  // product grid (was contact info, which had no data dependency), so calling renderForm()
  // at the top of the file, before CATEGORY_LABELS/cqSelectedGroups etc. are assigned,
  // crashed on load.
  if (quoteId) {
    loadPortal(quoteId);
  } else {
    renderForm();
  }

  // Shopify's CDN honors a `width` query param and serves back a resized/compressed
  // JPEG instead of the original upload (often several MB for these product photos) —
  // cuts real page weight instead of just deferring it with loading="lazy". Every image
  // on this form comes straight from the TSD Shopify CDN, so this is safe to apply
  // everywhere images are rendered.
  function cdnResize(url, width) {
    if (!url) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function groupEmoji(group) {
    var cats = group.categories || [];
    for (var i = 0; i < cats.length; i++) {
      if (CAT_EMOJI[cats[i]]) return CAT_EMOJI[cats[i]];
    }
    return '🥤';
  }

  function getGroupImages(group) {
    var urls = (group.image_urls || []).slice(0, 10);
    if (!urls.length && group.image_url) urls = [group.image_url];
    return urls;
  }

  function renderSlider(imgs, uid) {
    if (imgs.length === 0) return '';
    if (imgs.length === 1) {
      return '<img src="' + escHtml(cdnResize(imgs[0], 500)) + '" loading="lazy" draggable="false">';
    }
    var dots = imgs.map(function (_, i) {
      return '<span class="cq-dot' + (i === 0 ? ' cq-dot-active' : '') + '"></span>';
    }).join('');
    var slides = imgs.map(function (u) {
      return '<img src="' + escHtml(cdnResize(u, 500)) + '" loading="lazy" draggable="false">';
    }).join('');
    return [
      '<div class="cq-slider" data-uid="' + escHtml(uid) + '" data-idx="0">',
      '  <div class="cq-slider-track">' + slides + '</div>',
      '  <button type="button" class="cq-slider-arrow cq-slider-arrow-prev" aria-label="Previous photo">&#8249;</button>',
      '  <button type="button" class="cq-slider-arrow cq-slider-arrow-next" aria-label="Next photo">&#8250;</button>',
      '  <div class="cq-slider-dots">' + dots + '</div>',
      '</div>',
    ].join('');
  }

  function renderForm() {
    cqStep = 1;
    cqData = {};
    cqVisited = {};
    cqSoleApplied = {};
    cqLabelRules = null;
    cqSelectedGroups = {};
    cqActiveCat = 'popular';
    cqGroupOptions = {};
    renderStep();
  }

  var CQ_TOTAL_STEPS = 4;

  var CQ_STEP_NAMES = { 1: 'Products', 2: 'Print style', 3: 'Colors & sizes', 4: 'Order details' };

  // A one-line summary of what was answered on a step, for its trail bar.
  function stepSummary(n) {
    if (n === 1) {
      return Object.values(cqSelectedGroups).map(function (g) { return g.name; }).join(', ');
    }
    if (n === 2) {
      return DECO_LABELS[cqData.decoration_method || 'not_sure'] || '';
    }
    if (n === 3) {
      var picked = [];
      var anyNoPref = false;
      Object.keys(cqSelectedGroups).forEach(function (id) {
        var pref = (cqData.productPreferences || {})[id];
        if (!pref || pref.no_preference || !pref.variants.length) { anyNoPref = true; return; }
        pref.variants.forEach(function (v) { picked.push(String(v.label).replace(/(\d)\s*OZ\b/gi, '$1 oz')); });
      });
      if (!picked.length) return 'No preference';
      var text = picked.slice(0, 6).join(', ');
      if (picked.length > 6) text += ' +' + (picked.length - 6) + ' more';
      else if (anyNoPref) text += ' · no preference on the rest';
      return text;
    }
    return '';
  }

  // The steps already answered, stacked directly above the current one — the nearest bar
  // is the previous page, and any earlier bar jumps straight back to that step rather than
  // making the customer walk backwards one screen at a time.
  function renderTrail() {
    var done = [1, 2, 3, 4].filter(function (n) { return n < cqStep && cqVisited[n]; });
    if (!done.length) return '';
    return '<div class="cq-trail">' + done.map(function (n) {
      var sum = stepSummary(n);
      return '<button type="button" class="cq-trail-step" data-goto="' + n + '">' +
        '<span class="cq-trail-arrow" aria-hidden="true">←</span>' +
        '<span class="cq-trail-name">' + escHtml(CQ_STEP_NAMES[n]) + '</span>' +
        (sum ? '<span class="cq-trail-val">' + escHtml(sum) + '</span>' : '') +
        '<span class="cq-trail-edit">Change</span>' +
        '</button>';
    }).join('') + '</div>';
  }

  // The nearest step behind this one that the customer actually landed on — steps 2 and 3
  // are both conditionally skipped, so "back one screen" is not "step minus one".
  function prevStep() {
    for (var n = cqStep - 1; n >= 1; n--) { if (cqVisited[n]) return n; }
    return 1;
  }

  function goToStep(n) {
    // A changed answer can change which steps follow (step 2 and 3 are both conditionally
    // skipped), so anything recorded past the target is no longer a path that was taken.
    [1, 2, 3, 4].forEach(function (k) { if (k > n) delete cqVisited[k]; });
    cqStep = n;
    renderStep();
    scrollTop();
  }

  function renderStep() {
    cqVisited[cqStep] = true;
    var pct = Math.round(cqStep / CQ_TOTAL_STEPS * 100);
    var body;
    if (cqStep === 1) body = renderStep1();
    else if (cqStep === 2) body = renderStep2();
    else if (cqStep === 3) body = renderStep3();
    else body = renderStep4();
    container.innerHTML = [
      '<div class="cq-wrap">',
      '  <div class="cq-prog-bar"><div class="cq-prog-fill" style="width:' + pct + '%"></div></div>',
      renderTrail(),
      body,
      '</div>',
    ].join('\n');
    bindStep();
  }

  // ── Step 1: Product Group Selector ──────────────────────────────────────────

  function renderStep1() {
    var catTabsHtml = Object.keys(CATEGORY_LABELS).map(function (cat) {
      return '<button class="cq-cat-tab' + (cqActiveCat === cat ? ' active' : '') + '" data-cat="' + cat + '">' + CATEGORY_LABELS[cat] + '</button>';
    }).join('');

    var selCount = Object.keys(cqSelectedGroups).length;
    var selMsg = selCount > 0
      ? selCount + ' product' + (selCount > 1 ? 's' : '') + ' selected'
      : '';

    return [
      '<h2 class="cq-heading">What products are you interested in?</h2>',
      '<p class="cq-sub">Select all that apply — you can order more than one style.</p>',
      '<div class="cq-cat-tabs" id="cq-cat-tabs">' + catTabsHtml + '</div>',
      '<div id="cq-pg-grid" class="cq-pg-grid">',
      '  <p style="color:#888;padding:20px 0">Loading products…</p>',
      '</div>',
      '<div id="cq-error" class="cq-alert cq-alert-error" style="display:none"></div>',
      '<div class="cq-step-footer">',
      '  <span id="cq-sel-count" class="cq-sel-count">' + escHtml(selMsg) + '</span>',
      '  <button class="cq-btn cq-btn-primary" id="cq-next">Continue</button>',
      '  <span class="cq-step-count">1 of 4</span>',
      '</div>',
    ].join('\n');
  }

  function renderGroupGrid() {
    var gridEl = document.getElementById('cq-pg-grid');
    if (!gridEl || !cqGroups) return;

    var filtered = cqGroups.filter(function (g) {
      if (cqActiveCat === 'all') return true;
      return (g.categories || []).indexOf(cqActiveCat) !== -1;
    });

    if (!filtered.length) {
      gridEl.innerHTML = '<p style="color:#888;padding:20px 0;grid-column:1/-1">No products in this category.</p>';
      return;
    }

    gridEl.innerHTML = filtered.map(function (g) {
      var isSelected = !!cqSelectedGroups[g.id];
      var imgs = getGroupImages(g);
      var imgHtml = imgs.length
        ? renderSlider(imgs, g.id)
        : '<div class="cq-pg-emoji">' + groupEmoji(g) + '</div>';
      return [
        '<div class="cq-pg-card' + (isSelected ? ' cq-selected' : '') + '" data-id="' + escHtml(g.id) + '">',
        '  <div class="cq-pg-img-wrap">',
        '    ' + imgHtml,
        isSelected ? '    <div class="cq-pg-check">&#10003;</div>' : '',
        '  </div>',
        '  <div class="cq-pg-body">',
        '    <div class="cq-pg-name">' + escHtml(g.name) + '</div>',
        '    <div class="cq-pg-sub">' + escHtml(g.subtitle || '') + '</div>',
        '    <button class="cq-pg-info-btn" data-info="' + escHtml(g.id) + '">More info</button>',
        '  </div>',
        '</div>',
      ].join('\n');
    }).join('\n');

    bindGroupCards();
    bindSliders();
    updateSelCount();
  }

  function bindSliders() {
    document.querySelectorAll('.cq-slider').forEach(function (slider) {
      var track = slider.querySelector('.cq-slider-track');
      var dots = slider.querySelectorAll('.cq-dot');
      var count = track.children.length;
      if (count <= 1) return;
      var idx = 0;
      var startX = 0;
      var didSwipe = false;

      function goTo(n) {
        idx = ((n % count) + count) % count;
        track.style.transform = 'translateX(-' + (idx * 100) + '%)';
        dots.forEach(function (d, i) { d.classList.toggle('cq-dot-active', i === idx); });
        slider.dataset.idx = idx;
      }

      slider.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        didSwipe = false;
      }, { passive: true });

      slider.addEventListener('touchmove', function (e) {
        if (Math.abs(e.touches[0].clientX - startX) > 8) didSwipe = true;
      }, { passive: true });

      slider.addEventListener('touchend', function (e) {
        if (!didSwipe) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 30) {
          goTo(idx + (dx < 0 ? 1 : -1));
          slider._swiped = true;
          setTimeout(function () { slider._swiped = false; }, 350);
        }
      });

      var prevBtn = slider.querySelector('.cq-slider-arrow-prev');
      var nextBtn = slider.querySelector('.cq-slider-arrow-next');
      prevBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        goTo(idx - 1);
      });
      nextBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        goTo(idx + 1);
      });
    });
  }

  function bindGroupCards() {
    var gridEl = document.getElementById('cq-pg-grid');
    if (!gridEl) return;

    gridEl.querySelectorAll('.cq-pg-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.classList.contains('cq-pg-info-btn') || e.target.closest('.cq-pg-info-btn')) return;
        var slider = card.querySelector('.cq-slider');
        if (slider && slider._swiped) return;
        var id = card.dataset.id;
        var group = cqGroups.find(function (g) { return g.id === id; });
        if (!group) return;
        if (cqSelectedGroups[id]) {
          delete cqSelectedGroups[id];
          card.classList.remove('cq-selected');
          card.querySelector('.cq-pg-check') && card.querySelector('.cq-pg-check').remove();
        } else {
          cqSelectedGroups[id] = group;
          card.classList.add('cq-selected');
          var wrap = card.querySelector('.cq-pg-img-wrap');
          if (wrap && !wrap.querySelector('.cq-pg-check')) {
            var chk = document.createElement('div');
            chk.className = 'cq-pg-check';
            chk.innerHTML = '&#10003;';
            wrap.appendChild(chk);
          }
        }
        updateSelCount();
      });
    });

    gridEl.querySelectorAll('.cq-pg-info-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var id = btn.dataset.info;
        var group = cqGroups.find(function (g) { return g.id === id; });
        if (group) openModal(group);
      });
    });
  }

  function updateSelCount() {
    var el = document.getElementById('cq-sel-count');
    if (!el) return;
    var n = Object.keys(cqSelectedGroups).length;
    el.textContent = n > 0 ? n + ' product' + (n > 1 ? 's' : '') + ' selected' : '';
  }

  // ── Product Modal ───────────────────────────────────────────────────────────

  function openModal(group) {
    closeModal();
    var isSelected = !!cqSelectedGroups[group.id];
    var specs = group.specs || {};
    var specsHtml = Object.keys(specs).map(function (k) {
      return '<tr><td class="cq-modal-spec-key">' + escHtml(k) + '</td><td class="cq-modal-spec-val">' + escHtml(specs[k]) + '</td></tr>';
    }).join('');

    var swatches = group.color_swatches || [];
    var swatchesHtml = swatches.length ? (
      '<div class="cq-modal-swatches">' +
      '<div class="cq-modal-color-label">Color Options</div>' +
      '<div class="cq-swatch-row">' +
      swatches.map(function (s) {
        return '<img class="cq-swatch" src="' + escHtml(cdnResize(s.image_url, 100)) + '" alt="' + escHtml(s.name) + '" title="' + escHtml(s.name) + '" loading="lazy">';
      }).join('') +
      '</div></div>'
    ) : '';

    var modalImgs = getGroupImages(group);
    var imgHtml;
    if (modalImgs.length > 1) {
      var modalSlides = modalImgs.map(function (u) {
        return '<img src="' + escHtml(cdnResize(u, 800)) + '" class="cq-modal-img" loading="lazy" draggable="false">';
      }).join('');
      var modalDots = modalImgs.map(function (_, i) {
        return '<span class="cq-dot' + (i === 0 ? ' cq-dot-active' : '') + '"></span>';
      }).join('');
      imgHtml = '<div class="cq-modal-slider">' +
        '<div class="cq-modal-slider-track">' + modalSlides + '</div>' +
        '<button type="button" class="cq-slider-arrow cq-slider-arrow-prev" aria-label="Previous photo">&#8249;</button>' +
        '<button type="button" class="cq-slider-arrow cq-slider-arrow-next" aria-label="Next photo">&#8250;</button>' +
        '<div class="cq-slider-dots cq-modal-dots">' + modalDots + '</div></div>';
    } else if (modalImgs.length === 1) {
      imgHtml = '<img src="' + escHtml(cdnResize(modalImgs[0], 800)) + '" alt="' + escHtml(group.name) + '" class="cq-modal-img">';
    } else {
      imgHtml = '<div class="cq-modal-emoji">' + groupEmoji(group) + '</div>';
    }

    var btnLabel = isSelected ? '&#10003; Added — Remove' : 'Add to My Order';
    var btnClass = isSelected ? 'cq-btn-outline cq-modal-added' : 'cq-btn-primary';

    var overlay = document.createElement('div');
    overlay.id = 'cq-modal-overlay';
    overlay.className = 'cq-modal-backdrop';
    overlay.innerHTML = [
      '<div class="cq-modal" role="dialog" aria-modal="true">',
      '  <button class="cq-modal-close" id="cq-modal-close">&times;</button>',
      '  ' + imgHtml,
      '  <div class="cq-modal-content">',
      '    <h3 class="cq-modal-name">' + escHtml(group.name) + '</h3>',
      '    <p class="cq-modal-sub">' + escHtml(group.subtitle || '') + '</p>',
      specsHtml ? '    <table class="cq-modal-specs"><tbody>' + specsHtml + '</tbody></table>' : '',
      swatchesHtml,
      '    <button class="cq-btn ' + btnClass + ' cq-modal-toggle" id="cq-modal-toggle" data-id="' + escHtml(group.id) + '">' + btnLabel + '</button>',
      '  </div>',
      '</div>',
    ].join('\n');

    document.body.appendChild(overlay);

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });
    document.getElementById('cq-modal-close').addEventListener('click', closeModal);

    // Bind modal slider swipe
    var mSlider = overlay.querySelector('.cq-modal-slider');
    if (mSlider) {
      var mTrack = mSlider.querySelector('.cq-modal-slider-track');
      var mDots = mSlider.querySelectorAll('.cq-dot');
      var mCount = mTrack.children.length;
      var mIdx = 0, mStartX = 0, mDidSwipe = false;
      function mGoTo(n) {
        mIdx = ((n % mCount) + mCount) % mCount;
        mTrack.style.transform = 'translateX(-' + (mIdx * 100) + '%)';
        mDots.forEach(function (d, i) { d.classList.toggle('cq-dot-active', i === mIdx); });
      }
      mSlider.addEventListener('touchstart', function (e) { mStartX = e.touches[0].clientX; mDidSwipe = false; }, { passive: true });
      mSlider.addEventListener('touchmove', function (e) { if (Math.abs(e.touches[0].clientX - mStartX) > 8) mDidSwipe = true; }, { passive: true });
      mSlider.addEventListener('touchend', function (e) {
        if (!mDidSwipe) return;
        var dx = e.changedTouches[0].clientX - mStartX;
        if (Math.abs(dx) > 30) mGoTo(mIdx + (dx < 0 ? 1 : -1));
      });
      var mPrevBtn = mSlider.querySelector('.cq-slider-arrow-prev');
      var mNextBtn = mSlider.querySelector('.cq-slider-arrow-next');
      mPrevBtn.addEventListener('click', function (e) { e.stopPropagation(); mGoTo(mIdx - 1); });
      mNextBtn.addEventListener('click', function (e) { e.stopPropagation(); mGoTo(mIdx + 1); });
    }
    document.getElementById('cq-modal-toggle').addEventListener('click', function () {
      var id = group.id;
      if (cqSelectedGroups[id]) {
        delete cqSelectedGroups[id];
        this.textContent = 'Add to My Order';
        this.className = 'cq-btn cq-btn-primary cq-modal-toggle';
      } else {
        cqSelectedGroups[id] = group;
        this.innerHTML = '&#10003; Added — Remove';
        this.className = 'cq-btn cq-btn-outline cq-modal-added cq-modal-toggle';
      }
      renderGroupGrid();
      updateSelCount();
    });
  }

  function closeModal() {
    var existing = document.getElementById('cq-modal-overlay');
    if (existing) existing.remove();
  }

  // Fetches and caches /options for any of the given group ids not already in
  // cqGroupOptions. Shared by the step 1→2 transition (to decide whether the UV/wrap
  // page is even worth showing) and step 3's own lazy-load.
  // Settings carry the label rewrites step 3 needs, not just step 2's example images — and
  // step 2 is conditionally skipped, so fetching them there only would leave a customer who
  // skips it reading raw catalog wording. Resolved before leaving step 1 instead.
  function fetchSettings() {
    if (cqSettings) return Promise.resolve();
    return fetch(API_URL + '/api/settings')
      .then(function (r) { return r.json(); })
      .then(function (data) { cqSettings = data; })
      .catch(function () { cqSettings = {}; });
  }

  function fetchGroupOptions(ids) {
    var needed = ids.filter(function (id) { return cqGroupOptions[id] === undefined; });
    if (!needed.length) return Promise.resolve();
    return Promise.all(needed.map(function (id) {
      return fetch(API_URL + '/api/product-groups/' + encodeURIComponent(id) + '/options')
        .then(function (r) { return r.json(); })
        .then(function (data) { cqGroupOptions[id] = Array.isArray(data) ? data : []; })
        .catch(function () { cqGroupOptions[id] = []; });
    }));
  }

  // quote-app.js (manual Shopify theme upload) and routes/public.py (auto-deploys on
  // git push) can go live out of step — if this frontend ships before the backend's
  // `sublimatable` field does, every option comes back with it `undefined`, which would
  // silently read as "not sublimatable" everywhere and wrongly skip step 2 for every
  // product. Fall back to decoration_method in that case so a lagging deploy degrades to
  // the old (still-correct) behavior instead of breaking the whole step.
  function isSublimatable(o) {
    return o.sublimatable !== undefined ? o.sublimatable : o.decoration_method === 'sublimation';
  }

  // A group only presents a real UV-vs-sublimation choice if it actually stocks SKUs in
  // both — otherwise the "what's your style preference" page has nothing to ask.
  function groupHasBothDecoMethods(groupId) {
    var opts = cqGroupOptions[groupId] || [];
    var hasUv = opts.some(function (o) { return !isSublimatable(o); });
    var hasSub = opts.some(function (o) { return isSublimatable(o); });
    return hasUv && hasSub;
  }

  // ── Step 2: Style preference + details ──────────────────────────────────────

  function renderStep2() {
    var selectedNames = Object.values(cqSelectedGroups).map(function (g) { return g.name; }).join(', ');
    var s = cqSettings || {};
    var uvUrl = s.uv_print_example_url || '';
    var subUrl = s.sub_example_url || '';

    function decoCard(value, label, desc, imgUrl) {
      var sel = cqData.decoration_method === value;
      var imgHtml = imgUrl
        ? '<img src="' + escHtml(imgUrl) + '" class="cq-deco-img" loading="lazy">'
        : '<div class="cq-deco-placeholder">' + (value === 'uv_print' ? '🖨️' : '🎨') + '</div>';
      return '<div class="cq-deco-card' + (sel ? ' cq-selected' : '') + '" data-deco="' + value + '">' +
        imgHtml +
        '<div class="cq-deco-label">' + escHtml(label) + '</div>' +
        '<div class="cq-deco-desc">' + escHtml(desc) + '</div>' +
        '</div>';
    }

    return [
      '<h2 class="cq-heading">What\'s your style preference?</h2>',
      '<p class="cq-sub">Pick the print style that fits your vision, or select no preference and we\'ll show you options.</p>',
      selectedNames ? '<div class="cq-selected-summary"><strong>Selected products:</strong> ' + escHtml(selectedNames) + '</div>' : '',
      '<div class="cq-deco-grid">',
      decoCard('uv_print', 'UV Print', 'Best for logos, text, and bold solid colors', uvUrl),
      decoCard('sublimation', 'Full Wrap Sublimation', 'Best for all-over patterns and vibrant color designs', subUrl),
      '</div>',
      '<div style="text-align:center;margin:12px 0 24px">',
      '  <button class="cq-no-pref-btn' + (cqData.decoration_method === 'not_sure' ? ' active' : '') + '" id="cq-no-pref">No preference — show me both options</button>',
      '</div>',
      '<div id="cq-error" class="cq-alert cq-alert-error" style="display:none"></div>',
      '<div class="cq-step-footer">',
      '  <button class="cq-btn cq-btn-outline" id="cq-back">← Back</button>',
      '  <button class="cq-btn cq-btn-primary" id="cq-next">Continue</button>',
      '  <span class="cq-step-count">2 of 4</span>',
      '</div>',
    ].join('\n');
  }

  // ── Step 3: Color / Size Preference (per selected product group) ───────────

  function ensureProductPreference(groupId) {
    if (!cqData.productPreferences) cqData.productPreferences = {};
    if (!cqData.productPreferences[groupId]) {
      cqData.productPreferences[groupId] = { no_preference: false, variants: [] };
    }
    return cqData.productPreferences[groupId];
  }

  // A variant's label/meta/key — pulled out since both the picker cards and the submitted
  // preference need the same "Color — Size" text, and sku is the one field guaranteed
  // unique per real catalog combo (color/size alone can collide or be blank).
  // Products actually made of glass read "Glass" as a material adjective ("Glass Can
  // Tumbler", "Glass Shot Glass"), which is what separates them from a stainless "Tumbler
  // Shot Glass" — the difference that decides whether "Clear" means silver or means clear.
  function isGlassProduct(title) {
    return /\bglass\s+\S/i.test(String(title || ''));
  }

  function escapeRegExp(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Option-label rewrites, edited by staff on the admin Form Settings page (one
  // "find = replace" per line) and served with the rest of /api/settings. The catalog's
  // wording drifts as the product line changes, and this is the lever for following it
  // without a code change — the deploy for this file is a manual Shopify theme upload.
  // Falls back to the seeded rule while settings are still loading, so a slow or failed
  // fetch degrades to today's behaviour rather than to raw catalog text.
  var DEFAULT_LABEL_RULES = [{ find: 'Clear', replace: 'Silver' }];
  var cqLabelRules = null;

  function labelRules() {
    if (cqLabelRules) return cqLabelRules;
    if (!cqSettings) return DEFAULT_LABEL_RULES;
    cqLabelRules = String(cqSettings.option_label_overrides || '')
      .split(/\n+/)
      .map(function (line) {
        var i = line.indexOf('=');
        if (i === -1) return null;
        var find = line.slice(0, i).trim();
        var replace = line.slice(i + 1).trim();
        return find ? { find: find, replace: replace } : null;
      })
      .filter(Boolean);
    return cqLabelRules;
  }

  function silverize(text, title) {
    if (!text) return text;
    var out = String(text);
    labelRules().forEach(function (rule) {
      // "Clear" is the one word whose meaning genuinely changes with the material, so it's
      // the one rule that skips glass. Any other rule staff add applies everywhere —
      // silently exempting glass from an unrelated rewrite would be baffling.
      if (/^clear$/i.test(rule.find) && isGlassProduct(title)) return;
      out = out.replace(new RegExp('\\b' + escapeRegExp(rule.find) + '\\b', 'gi'), rule.replace);
    });
    return out;
  }

  function variantLabel(o) {
    var color = silverize(o.color, o.title);
    return [color, o.size].filter(Boolean).join(' — ') || o.sku;
  }

  // "20OZ" → "20 oz" — the raw parsed value is all-caps and unspaced, which reads as a part
  // number rather than a size when it's used as a heading rather than a small meta line.
  function prettySize(size) {
    if (!size) return '';
    var m = /^(\d+(?:\.\d+)?)\s*OZ$/i.exec(size.trim());
    return m ? m[1] + ' oz' : size;
  }

  // Drop the size out of a listing name — it's the section heading, so "20oz Powder Coated
  // Skinny Tumbler" under a "20 oz" heading says it twice. Handles the size sitting
  // mid-title ("Daydream Club - 40oz Powder Coated Big Swig Tumbler") too, then tidies the
  // separator the removal can leave stranded.
  function stripSize(title) {
    if (!title) return '';
    return String(title)
      .replace(/\b\d+(?:\.\d+)?\s*oz\b/i, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/^[\s\-–—]+|[\s\-–—]+$/g, '')
      .trim() || String(title);
  }

  // What actually distinguishes one un-coloured option from another is the part of its
  // product name the others don't share — "Sublimatable Skinny Tumbler" vs "Sublimatable
  // Skinny Tumbler - Matte" vs "Sublimatable Holographic Skinny Tumbler" differ by Matte
  // and Holographic, and nothing else. Drop every word all of them have in common and
  // that's what's left. The one whose name is entirely common words is the plain version
  // of the product, so it gets called "Standard" rather than an empty label.
  function distinguishingLabels(titles) {
    var tokenSets = titles.map(function (t) {
      return stripSize(t).split(/\s+/).filter(Boolean);
    });
    var common = null;
    tokenSets.forEach(function (toks) {
      var lower = toks.map(function (w) { return w.toLowerCase(); });
      if (common === null) { common = lower.slice(); return; }
      common = common.filter(function (w) { return lower.indexOf(w) !== -1; });
    });
    common = common || [];
    // Only worth stripping when the names really do share most of their words; with a
    // single option, or names with nothing in common, leave them exactly as they are.
    if (titles.length < 2 || !common.length) {
      return titles.map(function (t) { return stripSize(t); });
    }
    var out = tokenSets.map(function (toks) {
      var kept = toks.filter(function (w) { return common.indexOf(w.toLowerCase()) === -1; });
      var label = kept.join(' ').replace(/^[\s\-–—]+|[\s\-–—]+$/g, '').trim();
      return label || 'Standard';
    });
    // Two products whose names differ only in words all of them share would come out with
    // the same label, which is worse than a long one — a customer can't tell two identical
    // chips apart. Fall back to the full names for the whole set rather than for the pair,
    // so the row stays internally consistent.
    var unique = {};
    var collides = out.some(function (l) {
      var k = l.toLowerCase();
      if (unique[k]) return true;
      unique[k] = true;
      return false;
    });
    return collides ? titles.map(function (t) { return stripSize(t); }) : out;
  }

  var DECO_LABELS = {
    uv_print: 'UV Print',
    sublimation: 'Full Wrap Sublimation',
    not_sure: 'No preference',
  };
  var DECO_OPPOSITE = { uv_print: 'sublimation', sublimation: 'uv_print' };

  // The options a group offers in a given print style — keyed off the SKU title's own
  // "Sublimatable" text, not the decoration_method field, since the title is the reliable
  // signal (see routes/public.py:get_group_options). Deduped by sku.
  function optionsInMethod(groupId, deco) {
    var options = cqGroupOptions[groupId];
    if (!options) return options === undefined ? undefined : [];
    var filtered = options;
    if (deco === 'uv_print') {
      filtered = options.filter(function (o) { return !isSublimatable(o); });
    } else if (deco === 'sublimation') {
      filtered = options.filter(function (o) { return isSublimatable(o); });
    }
    var seen = {};
    return filtered.filter(function (o) {
      if (seen[o.sku]) return false;
      seen[o.sku] = true;
      return true;
    });
  }

  // This deliberately does NOT fall back to showing every option when the chosen method
  // rules them all out. It used to, which quietly offered a customer who picked UV Print a
  // grid of sublimation-only blanks with nothing saying so. A group with nothing in the
  // chosen method now says exactly that and points at the method that does have stock.
  function filteredOptionsFor(groupId) {
    return optionsInMethod(groupId, cqData.decoration_method);
  }

  // A selected group with zero options in the chosen print style, but stock in the other
  // one — the case that earns the explanatory panel on step 3.
  function groupUnavailableInMethod(groupId) {
    var deco = cqData.decoration_method;
    if (!DECO_OPPOSITE[deco]) return false;
    var here = optionsInMethod(groupId, deco);
    if (here === undefined || here.length) return false;
    var there = optionsInMethod(groupId, DECO_OPPOSITE[deco]);
    return !!(there && there.length);
  }

  // A product that comes exactly one way isn't a choice, so it isn't presented as one —
  // it's recorded for the team and shown back as a confirmation line.
  function autoSelectSoleOptions() {
    Object.keys(cqSelectedGroups).forEach(function (id) {
      var opts = filteredOptionsFor(id);
      if (!opts || opts.length !== 1) return;
      if (cqSoleApplied[id] === opts[0].sku) return; // already applied; respect a deselect
      cqSoleApplied[id] = opts[0].sku;
      var pref = ensureProductPreference(id);
      pref.no_preference = false;
      pref.variants = [{ sku: opts[0].sku, label: variantLabel(opts[0]) }];
    });
  }

  // Whether step 3 has anything to ask. Every selected product coming exactly one way
  // means the step would be a page of statements with nothing to click.
  function stepThreeHasChoices() {
    return Object.keys(cqSelectedGroups).some(function (id) {
      // An unavailable group counts: skipping the step would hide the one screen that
      // explains why the customer's print style leaves them nothing for that product.
      if (groupUnavailableInMethod(id)) return true;
      var opts = filteredOptionsFor(id);
      return opts && opts.length > 1;
    });
  }

  // Where to go after the style step: step 3 when at least one selected product actually
  // offers a choice, otherwise straight past it. Every product coming exactly one way
  // makes step 3 a page of statements with nothing to click, so it records the sole
  // options for the team and gets out of the customer's way.
  function advanceFromStyle() {
    if (stepThreeHasChoices()) return 3;
    autoSelectSoleOptions();
    return 4;
  }

  // A label short enough to read as an attribute value ("Silver", "Camo", "White · Duo")
  // rather than a product name. It's the only thing the wide Style row was ever for —
  // "Sublimatable Holographic Skinny Tumbler" doesn't fit a colour chip; "Silver" does.
  var SHORT_LABEL_MAX = 18;
  function isShortLabel(l) { return String(l || '').length <= SHORT_LABEL_MAX; }

  function renderGroupOptionsSection(group) {
    var pref = ensureProductPreference(group.id);
    var options = cqGroupOptions[group.id];

    function shell(inner, extraCls, headExtra) {
      return [
        '<div class="cq-opt-group' + (extraCls || '') + '">',
        '  <div class="cq-opt-group-head">',
        '    <div class="cq-opt-group-name">' + escHtml(group.name) + '</div>',
        headExtra || '',
        '  </div>',
        inner,
        '</div>',
      ].join('\n');
    }

    if (options === undefined) {
      return shell('  <p class="cq-opt-loading">Loading options…</p>');
    }

    var filtered = filteredOptionsFor(group.id) || [];

    if (!filtered.length && groupUnavailableInMethod(group.id)) {
      var other = DECO_OPPOSITE[cqData.decoration_method];
      var otherCount = (optionsInMethod(group.id, other) || []).length;
      return shell([
        '  <div class="cq-opt-unavailable">',
        '    <p class="cq-opt-unavailable-msg">No <strong>' + escHtml(DECO_LABELS[cqData.decoration_method]) + '</strong> products are available for this product group.</p>',
        '    <button type="button" class="cq-btn cq-btn-outline cq-opt-switch" data-switch="' + escHtml(other) + '">',
        '      Switch to ' + escHtml(DECO_LABELS[other]) + ' to see ' + otherCount + ' option' + (otherCount === 1 ? '' : 's'),
        '    </button>',
        '    <p class="cq-opt-unavailable-note">Switching changes the print style for every product you selected. You can also leave it as is — we\'ll follow up with options for this one.</p>',
        '  </div>',
      ].join('\n'));
    }

    if (!filtered.length) {
      return shell('  <p class="cq-opt-none">No specific color/size options to choose from for this product — we\'ll follow up with choices.</p>');
    }

    function card(o, label, wide) {
      var sel = pref.variants.some(function (v) { return v.sku === o.sku; });
      // Main product image, not the color swatch — a swatch is a flat color chip, not a
      // photo of the actual product being ordered. Resized off Shopify's CDN, since these
      // cards render small and the originals can be several MB.
      var imgHtml = o.image_url
        ? '<span class="cq-opt-variant-img"><img src="' + escHtml(cdnResize(o.image_url, 200)) + '" alt="' + escHtml(variantLabel(o)) + '" loading="lazy"></span>'
        : '<span class="cq-opt-variant-img cq-opt-variant-noimg">🥤</span>';
      // Some listings put the size in the Shopify variant field rather than the title
      // (Hydro Bottles' colors are "18Oz"/"25Oz"/…), so a label can be a size too.
      label = label ? String(label).replace(/(\d)\s*OZ\b/gi, '$1 oz') : label;
      return '<button type="button" class="cq-opt-variant' + (wide ? ' cq-opt-wide' : '') + (sel ? ' cq-selected' : '') + '"' +
        ' data-sku="' + escHtml(o.sku) + '" data-label="' + escHtml(variantLabel(o)) + '">' +
        imgHtml +
        (label ? '<span class="cq-opt-variant-label">' + escHtml(label) + '</span>' : '') +
        '<span class="cq-opt-check" aria-hidden="true">✓</span>' +
        '</button>';
    }

    // ── One option: pre-selected rather than presented as a decision ──────────
    // autoSelectSoleOptions() has already recorded it. It stays a live card, though —
    // a customer who doesn't want this product can click it off (which drops it from the
    // quote), and the card stays put so a mis-click is one click to undo.
    if (filtered.length === 1) {
      var solePref = pref.variants.length ? '1 selected' : 'Removed from quote';
      var soleLabel = silverize(filtered[0].color, filtered[0].title) || stripSize(filtered[0].title || filtered[0].sku);
      // A one-word label ("Silver") in a wide name-sized card is a lot of empty box; a full
      // product name in a narrow one wraps to four lines. Pick the card to fit the label.
      var soleWide = !isShortLabel(soleLabel);
      return shell(
        '  <div class="cq-opt-products" data-sole="1" data-group="' + escHtml(group.id) + '">' +
        '    <div class="cq-opt-section"><div class="cq-opt-line"><div class="cq-opt-variant-grid' + (soleWide ? ' cq-opt-grid-wide' : '') + '">' +
        card(filtered[0], soleLabel, soleWide) +
        '    </div></div></div>' +
        '  </div>' +
        '  <p class="cq-opt-sole-note">Only one option for this product, so it\'s already selected. Click it to leave it off your quote.</p>',
        '',
        '    <span class="cq-opt-count" data-count="' + escHtml(group.id) + '">' + escHtml(solePref) + '</span>'
      );
    }

    // Leading number off the parsed size ("20OZ" → 20) — smaller sizes first.
    function sizeNum(size) {
      var m = /^(\d+(?:\.\d+)?)/.exec(size || '');
      return m ? parseFloat(m[1]) : Infinity;
    }

    // ── Bucket by size first ────────────────────────────────────────────────
    // Size is the top-level axis of this step: a customer shopping the Big Swig group
    // wants every 30oz option in one place, then every 40oz option — not scattered across
    // a box per product listing with the other size's options in between.
    var bySize = {};
    var sizesSeen = [];
    var unsized = [];
    filtered.forEach(function (o) {
      if (o.size) {
        if (!bySize[o.size]) { bySize[o.size] = []; sizesSeen.push(o.size); }
        bySize[o.size].push(o);
      } else {
        unsized.push(o);
      }
    });
    sizesSeen.sort(function (a, b) { return sizeNum(a) - sizeNum(b); });

    // Size headings only earn their space when there's an actual choice of size. A group
    // that's all one size (Little Swig, 14oz) or has no parseable size at all (Speaker Can
    // Coolers) renders as one plain section instead — with the single size, if there is
    // one, stated once up in the group header.
    var useSizeHeads = sizesSeen.length > 1;
    var sections;
    if (useSizeHeads) {
      sections = sizesSeen.map(function (sz) {
        return { head: prettySize(sz), items: bySize[sz] };
      });
      // Genuinely sizeless items in an otherwise-sized group (chapstick holders under Mini
      // Tumblers, handle bottles under Hydro) go last rather than being forced into a size.
      if (unsized.length) sections.push({ head: 'Other', items: unsized });
    } else {
      sections = [{ head: '', items: filtered }];
    }

    // ── Within a size, group by the quality that actually varies ─────────────
    // Not by product listing: a customer picking a 40oz Big Swig sees one row of colours,
    // not the same colour row split three ways because the catalog happens to file them
    // under three listings. What varies is colour, or — where a product comes in one
    // colour but several finishes/builds (Mini Skinny: Matte, Holographic, Glow) — style.
    // A bare-stainless option is the silver colourway of its powder-coated siblings, not a
    // separate "style": the plain 4 in 1 Can Cooler is the stainless version of the powder
    // coated ones right above it, same as the plain Big Swig next to its eleven colours.
    // The test is on the names, which is exactly what makes it true — "4 in 1 Can Cooler"
    // is "4 in 1 Powder Coated Can Cooler" with the coating words taken out, i.e. it adds
    // no word of its own. "Duo Skinny Tumbler" does add one ("Duo"), so it stays a style.
    function addsNoWordTo(plainOpt, coloredOpts) {
      var words = stripSize(plainOpt.title || '').toLowerCase().split(/\s+/).filter(Boolean);
      if (!words.length) return false;
      return coloredOpts.some(function (c) {
        var theirs = stripSize(c.title || '').toLowerCase().split(/\s+/).filter(Boolean);
        return words.every(function (w) { return theirs.indexOf(w) !== -1; });
      });
    }

    // The plainest option in a set: its name adds no word the others don't already have
    // ("Big Swig Tumbler" against "Powder Coated Big Swig Tumbler"), or it was already
    // identified as the featureless one by name-stripping.
    function isBaseOption(item, allOpts) {
      if (item.label === 'Standard') return true;
      var others = allOpts.filter(function (o) { return o !== item.opt; });
      return others.length > 0 && addsNoWordTo(item.opt, others);
    }

    function isStainlessBaseOf(plainOpt, coloredOpts) {
      return !isSublimatable(plainOpt) && addsNoWordTo(plainOpt, coloredOpts);
    }

    function renderSection(sec) {
      var colored = sec.items.filter(function (o) { return !!o.color; });
      var plain = sec.items.filter(function (o) { return !o.color; });

      // Promote a stainless base with no colour of its own into the colour row as Silver.
      // Mostly a no-op now that staff set `color_override` on those SKUs directly, but it
      // still covers any group where that hasn't been done.
      if (colored.length) {
        var promoted = plain.filter(function (o) { return isStainlessBaseOf(o, colored); });
        if (promoted.length) {
          plain = plain.filter(function (o) { return promoted.indexOf(o) === -1; });
          colored = promoted.map(function (o) {
            // Shallow copy so the cached /options payload keeps its real (empty) colour —
            // cqGroupOptions is reused across renders and re-entered on every Back.
            var copy = {};
            Object.keys(o).forEach(function (k) { copy[k] = o[k]; });
            copy.color = 'Silver';
            return copy;
          }).concat(colored);
        }
      }

      // Label the colourless options by what distinguishes them — computed against **every**
      // title in the section, coloured ones included, not just against each other. A section
      // where staff have set `color_override` on all but one SKU (the sublimatable Clear Big
      // Swig next to its White sibling) leaves that one alone in the colourless set with
      // nothing to strip against, so it fell back to its entire product name.
      var plainLabels = [];
      if (plain.length) {
        var allTitles = colored.concat(plain).map(function (o) { return o.title || o.sku; });
        var allLabels = distinguishingLabels(allTitles);
        plainLabels = plain.map(function (o, i) {
          return silverize(allLabels[colored.length + i], o.title);
        });
        // The one option that genuinely adds nothing to its siblings' names is the plain
        // version — "Big Swig Tumbler" next to eleven powder-coated colours doesn't say
        // that and "Standard" does. A leftover that *does* add a word (the Duo Skinny) is a
        // real variation, and calling that "Standard" would be a lie.
        if (plain.length === 1 && colored.length && addsNoWordTo(plain[0], colored)) plainLabels = ['Standard'];
      }

      // Anything whose label came out short belongs on the same row as the colours — it's
      // an attribute value like they are, and splitting "Silver" onto its own "Style" row
      // under a "Color" row holding only "White" reads as two unrelated questions.
      var merged = [];
      var longPlain = [];
      plain.forEach(function (o, i) {
        (isShortLabel(plainLabels[i]) ? merged : longPlain).push({ opt: o, label: plainLabels[i] });
      });

      var lines = [];
      var swatchItems = colored.map(function (o) { return { opt: o, label: null }; }).concat(merged);

      if (swatchItems.length) {
        // Flattened across listings, so two listings can contribute the same colour name
        // (the plain and the Duo Skinny are both "Silver"). Only those get a suffix, and
        // the suffix is the part of the product name the other one doesn't share.
        var byColor = {};
        swatchItems.forEach(function (it) {
          if (!it.opt.color) return;
          var k = String(it.opt.color).toLowerCase();
          (byColor[k] = byColor[k] || []).push(it.opt);
        });
        // The qualifier is worked out per clashing colour, over just the items that clash.
        // Running it over the whole row instead let unrelated same-titled SKUs elsewhere in
        // the row trip the collision guard and drag every qualifier back to a full title.
        var qualifier = {};
        Object.keys(byColor).forEach(function (k) {
          var items = byColor[k];
          if (items.length < 2) return;
          var labels = distinguishingLabels(items.map(function (o) { return o.title || o.sku; }));
          items.forEach(function (o, i) { qualifier[o.sku] = silverize(labels[i], o.title); });
        });

        // The plainest option leads the row — the bare stainless one on the UV side, the
        // plain white blank on the sublimation side. Left to catalog order it landed
        // wherever, which for a customer scanning colours is the one card that should be
        // easy to find. Sort is stable, so everything else keeps its catalog order.
        var others = swatchItems.map(function (it) { return it.opt; });
        var ordered = swatchItems.slice();
        ordered.sort(function (a, b) {
          var ab = isBaseOption(a, others) ? 0 : 1;
          var bb = isBaseOption(b, others) ? 0 : 1;
          return ab - bb;
        });

        lines.push({
          axis: 'Color',
          count: ordered.length + ' option' + (ordered.length === 1 ? '' : 's'),
          wide: false,
          cards: ordered.map(function (it) {
            var name = it.label || silverize(it.opt.color, it.opt.title);
            return card(it.opt, qualifier[it.opt.sku] ? name + ' · ' + qualifier[it.opt.sku] : name, false);
          }).join(''),
        });
      }

      if (longPlain.length) {
        longPlain.sort(function (a, b) {
          return (a.label === 'Standard' ? 0 : 1) - (b.label === 'Standard' ? 0 : 1);
        });
        lines.push({
          axis: 'Style',
          count: longPlain.length + ' option' + (longPlain.length === 1 ? '' : 's'),
          wide: true,
          cards: longPlain.map(function (it) { return card(it.opt, it.label, true); }).join(''),
        });
      }

      var showAxis = lines.length > 1;

      var linesHtml = lines.map(function (ln) {
        return [
          '<div class="cq-opt-line">',
          showAxis ? '  <div class="cq-opt-line-label">' + escHtml(ln.axis) + '<span class="cq-opt-line-count">' + escHtml(ln.count) + '</span></div>' : '',
          '  <div class="cq-opt-variant-grid' + (ln.wide ? ' cq-opt-grid-wide' : '') + '">' + ln.cards + '</div>',
          '</div>',
        ].join('');
      }).join('');

      return [
        '<div class="cq-opt-section">',
        sec.head ? '  <div class="cq-opt-size-head"><span class="cq-opt-axis">Size</span>' + escHtml(sec.head) + '<span class="cq-opt-size-count">' + sec.items.length + ' option' + (sec.items.length === 1 ? '' : 's') + '</span></div>' : '',
        linesHtml,
        '</div>',
      ].join('');
    }

    var selCount = pref.variants.length;
    var countText = pref.no_preference
      ? 'No preference'
      : (selCount ? selCount + ' selected' : 'None selected yet');
    // Only claim a single size for the whole group when *every* option actually carries
    // it. Can Coolers has one 12oz chiller among thirteen sizeless SKUs — one distinct
    // size, but "Can Coolers · 12 oz" would be a flat lie about the other thirteen.
    var loneSize = (!useSizeHeads && sizesSeen.length === 1 && !unsized.length)
      ? '<span class="cq-opt-group-size">' + escHtml(prettySize(sizesSeen[0])) + '</span>' : '';

    var head = [
      loneSize,
      '    <span class="cq-opt-count" data-count="' + escHtml(group.id) + '">' + escHtml(countText) + '</span>',
      '    <button type="button" class="cq-nopref-chip' + (pref.no_preference ? ' active' : '') + '" data-nopref="' + escHtml(group.id) + '">' +
           (pref.no_preference ? '✓ No preference' : 'No preference') + '</button>',
    ].join('\n');

    return shell(
      '  <div class="cq-opt-products" data-group="' + escHtml(group.id) + '">' + sections.map(renderSection).join('') + '</div>',
      pref.no_preference ? ' cq-opt-nopref' : '',
      head
    );
  }


  function renderStep3() {
    autoSelectSoleOptions();
    var groups = Object.values(cqSelectedGroups);
    var sections = groups.map(renderGroupOptionsSection).join('\n');

    return [
      '<h2 class="cq-heading">Choose your colors & sizes</h2>',
      '<p class="cq-sub">Pick specific options for each product below, or tell us you have no preference and we\'ll show you a few choices.</p>',
      sections,
      '<div id="cq-error" class="cq-alert cq-alert-error" style="display:none"></div>',
      '<div class="cq-step-footer">',
      '  <button class="cq-btn cq-btn-outline" id="cq-back">← Back</button>',
      '  <button class="cq-btn cq-btn-primary" id="cq-next">Continue</button>',
      '  <span class="cq-step-count">3 of 4</span>',
      '</div>',
    ].join('\n');
  }

  // ── Step 4: Contact Info + Order Details (qty, notes, artwork) ─────────────

  var CQ_SUBMIT_LABEL = 'Get Free Mockups & Quote →';

  function renderStep4() {
    return [
      '<h2 class="cq-heading">Order Details</h2>',
      '<p class="cq-sub">A few details and we\'ll get your free mockups and quote started.</p>',

      // Two required fields marked once here beats five "(optional)" tags scattered down
      // the form — the red * already carries it, and the repeated tag was the single
      // noisiest thing on this step.
      '<div class="cq-section-head">',
      '  <div class="cq-section-label">Contact</div>',
      '  <span class="cq-section-note">Only name and email are required</span>',
      '</div>',
      '<div class="cq-row">',
      '  <div class="cq-field"><label for="cq-name">Name <span class="cq-req">*</span></label>',
      '    <input type="text" id="cq-name" autocomplete="name" value="' + escHtml(cqData.customer_name || '') + '"></div>',
      '  <div class="cq-field"><label for="cq-email">Email <span class="cq-req">*</span></label>',
      '    <input type="email" id="cq-email" autocomplete="email" value="' + escHtml(cqData.customer_email || '') + '"></div>',
      '</div>',
      '<div class="cq-row">',
      '  <div class="cq-field"><label for="cq-phone">Phone</label>',
      '    <input type="tel" id="cq-phone" autocomplete="tel" value="' + escHtml(cqData.customer_phone || '') + '"></div>',
      '  <div class="cq-field"><label for="cq-company">Company</label>',
      '    <input type="text" id="cq-company" autocomplete="organization" value="' + escHtml(cqData.company_name || '') + '"></div>',
      '</div>',

      // "Your Order" repeated the step's own title; this section is about the job, not the
      // order record.
      '<div class="cq-section-head">',
      '  <div class="cq-section-label">Your Project</div>',
      '</div>',
      // In a row with an empty second column so it lines up with Name/Phone above rather
      // than sitting at an arbitrary width of its own, and the "an estimate is fine" note
      // rides on the label instead of dangling under the box.
      '<div class="cq-row">',
      '  <div class="cq-field">',
      '    <label for="cq-qty">How many units? <span class="cq-opt">an estimate is fine</span></label>',
      '    <input type="text" inputmode="numeric" pattern="[0-9]*" id="cq-qty" placeholder="e.g. 50" value="' + escHtml(cqData.quantity || '') + '">',
      '  </div>',
      '  <div class="cq-field cq-field-blank"></div>',
      '</div>',
      '<div class="cq-field">',
      '  <label for="cq-notes">Anything else we should know?</label>',
      '  <textarea id="cq-notes" rows="3" placeholder="Colors, placement, event details, design ideas...">' + escHtml(cqData.customization_notes || '') + '</textarea>',
      '</div>',
      '<div class="cq-field">',
      '  <label for="cq-files">Artwork or logo files</label>',
      // The raw file input renders as an unstyled OS control in the middle of a set of
      // custom-styled fields; the real input is kept (so nothing about submit changes) and
      // just hidden behind a label styled like the rest of the form.
      '  <input type="file" id="cq-files" multiple class="cq-file-input">',
      '  <label for="cq-files" class="cq-file-drop">',
      '    <span class="cq-file-drop-icon">📎</span>',
      '    <span><strong>Choose files</strong> — JPG, PNG, PDF, AI, EPS, or any design file.</span>',
      '  </label>',
      '  <div class="cq-file-list" id="cq-file-list"></div>',
      '</div>',
      '<div id="cq-error" class="cq-alert cq-alert-error" style="display:none"></div>',
      '<div class="cq-submit-section">',
      '  <button class="cq-btn cq-btn-primary cq-btn-submit" id="cq-submit">' + CQ_SUBMIT_LABEL + '</button>',
      '  <p class="cq-submit-note">Free mockups and a quote for every product you selected — no commitment.</p>',
      '</div>',
      '<div class="cq-step-footer" style="margin-top:0">',
      '  <button class="cq-btn cq-btn-outline" id="cq-back">← Back</button>',
      '  <span class="cq-step-count">4 of 4</span>',
      '</div>',
    ].join('\n');
  }

  function bindProductOptionInteractions() {
    document.querySelectorAll('.cq-opt-products').forEach(function (grid) {
      var groupId = grid.dataset.group;
      grid.querySelectorAll('.cq-opt-variant').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var pref = ensureProductPreference(groupId);
          if (pref.no_preference) return;
          var sku = btn.dataset.sku;
          var idx = pref.variants.findIndex(function (v) { return v.sku === sku; });
          if (idx === -1) pref.variants.push({ sku: sku, label: btn.dataset.label }); else pref.variants.splice(idx, 1);
          btn.classList.toggle('cq-selected');
          // Live count in the group header — the card wall is tall enough that a customer
          // can easily lose track of how much they've picked without scrolling back up.
          var countEl = document.querySelector('[data-count="' + CSS.escape(groupId) + '"]');
          // The sole-option group says "Removed from quote" rather than "None selected
          // yet" — there was never a selection to not-make, only one to take back.
          var empty = grid.dataset.sole ? 'Removed from quote' : 'None selected yet';
          if (countEl) countEl.textContent = pref.variants.length ? pref.variants.length + ' selected' : empty;
        });
      });
    });

    // Switching print style from inside step 3 changes the answer to step 2, so the
    // recorded path past that step is no longer this customer's path — same reasoning as
    // goToStep(). Re-render in place rather than bouncing them back to step 2: they asked
    // to see this group's options, not to redo the style question.
    document.querySelectorAll('[data-switch]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        cqData.decoration_method = btn.dataset.switch;
        cqSoleApplied = {};
        renderStep();
        scrollTop();
      });
    });

    document.querySelectorAll('[data-nopref]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var pref = ensureProductPreference(btn.dataset.nopref);
        pref.no_preference = !pref.no_preference;
        if (pref.no_preference) { pref.variants = []; }
        renderStep();
      });
    });
  }

  // ── Step binding ───────────────────────────────────────────────────────────

  function bindStep() {
    var errorEl = document.getElementById('cq-error');

    document.querySelectorAll('.cq-trail-step').forEach(function (bar) {
      bar.addEventListener('click', function () { goToStep(parseInt(bar.dataset.goto, 10)); });
    });

    if (cqStep === 1) {
      document.getElementById('cq-next').addEventListener('click', function () {
        var ids = Object.keys(cqSelectedGroups);
        if (ids.length === 0) {
          showError(errorEl, 'Please select at least one product.');
          return;
        }
        var nextBtn = this;
        nextBtn.disabled = true;
        var origLabel = nextBtn.textContent;
        nextBtn.textContent = 'Loading…';
        Promise.all([fetchGroupOptions(ids), fetchSettings()]).then(function () {
          nextBtn.disabled = false;
          nextBtn.textContent = origLabel;
          if (ids.some(groupHasBothDecoMethods)) {
            cqStep = 2;
          } else {
            // None of the selected products actually offer a UV/wrap choice — skip
            // straight to color/size instead of asking a question with no real answer.
            cqData.decoration_method = 'not_sure';
            cqStep = advanceFromStyle();
          }
          renderStep();
          scrollTop();
        });
      });

      // Category tabs
      document.getElementById('cq-cat-tabs').querySelectorAll('.cq-cat-tab').forEach(function (tab) {
        tab.addEventListener('click', function () {
          cqActiveCat = tab.dataset.cat;
          document.querySelectorAll('.cq-cat-tab').forEach(function (t) { t.classList.remove('active'); });
          tab.classList.add('active');
          renderGroupGrid();
        });
      });

      // Load product groups (fetch once, cache)
      if (!cqGroups) {
        fetch(API_URL + '/api/product-groups')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            cqGroups = data;
            renderGroupGrid();
          })
          .catch(function () {
            var gridEl = document.getElementById('cq-pg-grid');
            if (gridEl) gridEl.innerHTML = '<p style="color:#c00;grid-column:1/-1">Could not load products. Please refresh and try again.</p>';
          });
      } else {
        renderGroupGrid();
      }
    }

    if (cqStep === 2) {
      document.getElementById('cq-back').addEventListener('click', function () {
        goToStep(prevStep());
      });

      document.getElementById('cq-next').addEventListener('click', function () {
        if (!cqData.decoration_method) cqData.decoration_method = 'not_sure';
        cqStep = advanceFromStyle();
        renderStep();
        scrollTop();
      });

      // Normally already resolved at the step 1 → 2 transition; still here so a direct
      // re-entry to this step (Back, or a trail jump) can't render without the images.
      if (!cqSettings) {
        fetchSettings().then(function () { if (cqStep === 2) renderStep(); });
      }

      // Decoration card selection
      document.querySelectorAll('.cq-deco-card').forEach(function (card) {
        card.addEventListener('click', function () {
          document.querySelectorAll('.cq-deco-card').forEach(function (c) { c.classList.remove('cq-selected'); });
          document.getElementById('cq-no-pref').classList.remove('active');
          card.classList.add('cq-selected');
          cqData.decoration_method = card.dataset.deco;
        });
      });

      document.getElementById('cq-no-pref').addEventListener('click', function () {
        // "No preference" means the customer isn't picky about anything downstream
        // either — skip straight past color/size (step 3) instead of asking them to
        // narrow down products they already said they have no opinion on.
        cqData.decoration_method = 'not_sure';
        Object.keys(cqSelectedGroups).forEach(function (gid) {
          var pref = ensureProductPreference(gid);
          pref.no_preference = true;
          pref.variants = [];
        });
        cqStep = 4;
        renderStep();
        scrollTop();
      });
    }

    if (cqStep === 3) {
      document.getElementById('cq-back').addEventListener('click', function () {
        goToStep(prevStep());
      });

      document.getElementById('cq-next').addEventListener('click', function () {
        // A group nobody touched (no variants picked, "No preference" never clicked)
        // submits as an implicit no-preference rather than blocking Continue on it — this
        // step is meant to narrow things down when the customer has an opinion, not force one.
        Object.keys(cqSelectedGroups).forEach(function (gid) {
          var pref = ensureProductPreference(gid);
          if (!pref.no_preference && !pref.variants.length) {
            pref.no_preference = true;
          }
        });
        cqStep = 4;
        renderStep();
        scrollTop();
      });

      bindProductOptionInteractions();

      var ids3 = Object.keys(cqSelectedGroups);
      if (ids3.some(function (id) { return cqGroupOptions[id] === undefined; })) {
        fetchGroupOptions(ids3).then(function () {
          if (cqStep === 3) renderStep();
        });
      }
    }

    if (cqStep === 4) {
      document.getElementById('cq-back').addEventListener('click', function () {
        goToStep(prevStep());
      });

      var filesInput = document.getElementById('cq-files');
      var fileList = document.getElementById('cq-file-list');
      if (filesInput && fileList) {
        filesInput.addEventListener('change', function () {
          var names = [];
          for (var i = 0; i < filesInput.files.length; i++) names.push(filesInput.files[i].name);
          fileList.innerHTML = names.map(function (n) {
            return '<span class="cq-file-pill">' + escHtml(n) + '</span>';
          }).join('');
        });
      }

      document.getElementById('cq-submit').addEventListener('click', function () {
        var submitBtn = document.getElementById('cq-submit');
        errorEl.style.display = 'none';

        var name = (document.getElementById('cq-name').value || '').trim();
        var email = (document.getElementById('cq-email').value || '').trim();
        if (!name) { showError(errorEl, 'Please enter your name.'); return; }
        if (!email || !email.includes('@')) { showError(errorEl, 'Please enter a valid email address.'); return; }

        // Upper bound mirrors services/pricing.py's MAX_QUANTITY (50,000) — this box only
        // ever checked for < 1, so a phone number typed in here went straight through and
        // priced the whole quote off it on the admin side. Server enforces it too.
        var qty = (document.getElementById('cq-qty').value || '').trim();
        if (qty && parseInt(qty, 10) < 1) { showError(errorEl, 'Quantity must be at least 1.'); return; }
        if (qty && parseInt(qty, 10) > 50000) {
          showError(errorEl, "That doesn't look like a quantity — enter the number of units (up to 50,000).");
          return;
        }

        cqData.customer_name = name;
        cqData.customer_email = email;
        cqData.customer_phone = (document.getElementById('cq-phone').value || '').trim();
        cqData.company_name = (document.getElementById('cq-company').value || '').trim();
        cqData.quantity = qty;
        cqData.customization_notes = (document.getElementById('cq-notes').value || '').trim();
        if (!cqData.decoration_method) cqData.decoration_method = 'not_sure';

        submitBtn.disabled = true;
        submitBtn.textContent = 'Submitting…';

        var selectedIds = Object.keys(cqSelectedGroups).join(',');
        var selectedNames = Object.values(cqSelectedGroups).map(function (g) { return g.name; }).join(', ');

        var fd = new FormData();
        fd.append('customer_name', cqData.customer_name || '');
        fd.append('customer_email', cqData.customer_email || '');
        fd.append('customer_phone', cqData.customer_phone || '');
        fd.append('company_name', cqData.company_name || '');
        fd.append('product_id', selectedIds);
        fd.append('product_name', selectedNames);
        fd.append('quantity', cqData.quantity);
        // Stored as-is, including "not_sure" — silently converting it to a specific method
        // here meant every downstream display (admin, internal email) showed that guessed
        // method instead of what the customer actually said. The one place a concrete
        // choice is unavoidable (the Shopify invoice line item) makes that call itself,
        // at approval time, in shopify_service.py.
        fd.append('decoration_method', cqData.decoration_method);
        fd.append('customization_notes', cqData.customization_notes || '');
        fd.append('product_preferences', JSON.stringify(cqData.productPreferences || {}));

        var fileInput = document.getElementById('cq-files');
        if (fileInput && fileInput.files) {
          for (var i = 0; i < fileInput.files.length; i++) {
            fd.append('files[]', fileInput.files[i]);
          }
        }

        fetch(API_URL + '/api/quotes', { method: 'POST', body: fd })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (d) { throw new Error(d.error || 'Submission failed'); });
            return r.json();
          })
          .then(function (data) {
            if (data.quote_url) {
              window.location.href = data.quote_url;
            } else {
              renderSuccess(data.quote_url);
            }
          })
          .catch(function (err) {
            showError(errorEl, err.message || 'Something went wrong. Please try again.');
            submitBtn.disabled = false;
            submitBtn.textContent = CQ_SUBMIT_LABEL;
          });
      });
    }
  }

  function scrollTop() {
    if (container) container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function renderSuccess(quoteUrl) {
    container.innerHTML = [
      '<div class="cq-wrap">',
      '  <div class="cq-success-icon">&#10003;</div>',
      '  <h2 class="cq-heading">Request Received!</h2>',
      '  <p class="cq-sub">Thanks for reaching out. We\'ll review your request and email you when your mockups are ready — usually within 1–3 business days.</p>',
      '  <div class="cq-card">',
      '    <p style="margin:0 0 8px;font-size:11px;color:#182e49;font-weight:700;text-transform:uppercase;letter-spacing:.08em">Bookmark your quote link</p>',
      '    <div class="cq-copy-row">',
      '      <input type="text" id="cq-quote-url" value="' + escHtml(quoteUrl) + '" readonly>',
      '      <button class="cq-btn cq-btn-sm" onclick="cqCopy()">Copy</button>',
      '    </div>',
      '  </div>',
      '</div>',
    ].join('\n');

    window.cqCopy = function () {
      var inp = document.getElementById('cq-quote-url');
      inp.select();
      document.execCommand('copy');
      var btn = container.querySelector('.cq-copy-row .cq-btn');
      btn.textContent = 'Copied!';
      setTimeout(function () { btn.textContent = 'Copy'; }, 2000);
    };
  }

  // ── Portal view ────────────────────────────────────────────────────────────

  function loadPortal(id) {
    container.innerHTML = '<div class="cq-wrap"><p class="cq-sub" style="text-align:center;padding:32px 0">Loading your quote…</p></div>';
    fetch(API_URL + '/api/quotes/' + encodeURIComponent(id))
      .then(function (r) {
        if (r.status === 404) throw new Error('not_found');
        if (!r.ok) throw new Error('error');
        return r.json();
      })
      .then(function (quote) { renderPortal(quote); })
      .catch(function (err) {
        if (err.message === 'not_found') {
          container.innerHTML = '<div class="cq-wrap"><div class="cq-alert cq-alert-error">Quote not found. Please check the link and try again, or <a href="/pages/custom-quote">start a new request</a>.</div></div>';
        } else {
          container.innerHTML = '<div class="cq-wrap"><div class="cq-alert cq-alert-error">Could not load your quote. Please refresh the page or try again later.</div></div>';
        }
      });
  }

  // One consistent layout for the whole lifecycle (2026-07-20) — replaces three separate
  // full-page templates (renderPending/renderMockupReady/renderApproved) that each showed
  // different sections, so a customer checking back at different points saw what looked
  // like a different site. Now every status (except cancelled, a dead-end off-ramp outside
  // the normal flow) renders the same three pieces in the same order: a status-specific
  // header, a permanent Artwork & Mockups card, and a permanent Products & Pricing card —
  // only the header copy and the action button(s) below them change with status/opts.
  // `opts.confirmingQty` is the one exception to "no different pages": clicking Approve
  // reveals an inline quantity-editing step by re-rendering with that flag set, rather than
  // navigating to a separate screen — the Artwork area stays exactly as it was above it.
  function renderPortal(quote) {
    var status = quote.status;
    var html = '<div class="cq-wrap">';
    if (status === 'cancelled') {
      html += renderCancelled(quote);
    } else {
      html += renderStatusHeader(quote);
      html += renderArtworkArea(quote);
      html += renderSkuPricingArea(quote);
      html += '<div id="cq-portal-error" class="cq-alert cq-alert-error" style="display:none"></div>';
      html += renderActionsArea(quote);
    }
    html += '</div>';
    container.innerHTML = html;
    setupPortalListeners(quote);
  }

  // Artwork phase (2026-08-04, "clean up our phases that the order sits in... the artwork
  // should either show upload file, mockups in progress, mockups are ready/need changes.
  // those are the only 3 views for the artwork... at the top, either show awaiting artwork,
  // mockups in progress, or ready") — a single 3-state derivation shared by both
  // renderStatusHeader() (top) and renderArtworkArea() (bottom), driven by the actual
  // artwork/mockup *data* (artwork_urls/design_line_items), not the order's checkout/payment
  // status — "the bottom and top views can work totally separate from each other [from
  // checkout]". 'in_review' forces 'in_progress' regardless of whether old design_line_items
  // still exist ("if a customer requests edits it moves back from ready to in progress") —
  // request_edit() doesn't clear design_line_items itself (those stay as reference for staff
  // preparing the revision), so without this override a customer who requested changes would
  // still see their stale "ready" mockups as if nothing happened.
  function getArtworkPhase(quote) {
    var hasArtwork = quote.artwork_urls && quote.artwork_urls.length > 0;
    var hasDesignItems = quote.design_line_items && quote.design_line_items.length > 0;
    if (quote.status === 'in_review') return 'in_progress';
    if (hasDesignItems) return 'ready';
    if (hasArtwork) return 'in_progress';
    return 'upload';
  }

  // Top header (2026-08-04 rework) — checkout/payment status and artwork phase render
  // completely independently now. Once actually paid (invoiced/shipped), the header always
  // shows order-confirmation messaging regardless of artwork phase ("it should show order
  // confirmed... still show the mockup and price details, just don't link to an invoice" —
  // the "don't link" half lives in renderActionsArea()). Before payment, the header is
  // driven purely by getArtworkPhase() — never by submitted/in_review/mockup_ready/approved
  // individually, so "Approve and Complete Order" (below) becoming available doesn't change
  // what this says at all.
  function renderStatusHeader(quote) {
    var status = quote.status;
    if (status === 'invoiced' || status === 'shipped') {
      var isShipped = status === 'shipped';
      return [
        '<div class="cq-status-badge cq-status-approved">' + (isShipped ? 'Shipped' : 'Order Confirmed') + '</div>',
        isShipped
          ? '<h2 class="cq-heading">Your order has shipped!</h2>'
          : '<h2 class="cq-heading">Your order is confirmed!</h2>',
        isShipped
          ? '<p class="cq-sub">Hi ' + escHtml(quote.customer_name || 'there') + '! Your order is on its way.</p>'
          : '<p class="cq-sub">Hi ' + escHtml(quote.customer_name || 'there') + '! Thanks for your order — we\'ll be in touch with updates as it\'s produced and shipped.</p>',
      ].join('\n');
    }

    var phase = getArtworkPhase(quote);
    if (phase === 'upload') {
      return [
        '<div class="cq-status-badge cq-status-pending">Awaiting Artwork</div>',
        '<h2 class="cq-heading">Let\'s get your artwork</h2>',
        '<p class="cq-sub">Hi ' + escHtml(quote.customer_name || 'there') + '! Upload your logo or design file below and we\'ll get your mockups started.</p>',
      ].join('\n');
    }
    if (phase === 'in_progress') {
      return [
        '<div class="cq-status-badge cq-status-pending">Mockups In Progress</div>',
        '<h2 class="cq-heading">We\'re working on your mockups</h2>',
        '<p class="cq-sub">Hi ' + escHtml(quote.customer_name || 'there') + '! We\'ve received your artwork and are preparing your custom mockups. We\'ll send you an email at <strong>' + escHtml(quote.customer_email || '') + '</strong> when they\'re ready to review.</p>',
      ].join('\n');
    }
    // ready
    return [
      '<div class="cq-status-badge cq-status-ready">Ready</div>',
      '<h2 class="cq-heading">Your mockups are ready!</h2>',
      '<p class="cq-sub">Hi ' + escHtml(quote.customer_name || 'there') + '! Review your mockups below. If you\'d like changes, let us know — otherwise, confirm your quantities and complete your order whenever you\'re ready.</p>',
    ].join('\n');
  }

  // Permanent "Artwork & Mockups" card (2026-08-04 rework, "the artwork should either show
  // upload file, mockups in progress, mockups are ready/need changes. those are the only 3
  // views for the artwork") — exactly the 3 views getArtworkPhase() defines, driven by the
  // same phase the top header uses, entirely independent of checkout/payment status.
  //
  // No separate "Approve Design" step (removed 2026-07-21, explicit ask) — a mockup is
  // assumed good until the customer says otherwise via Request Changes below; the only
  // action left once mockups are ready is Approve and Complete Order in the actions area.
  //
  // Request Changes only shows in the 'ready' phase, and only before payment — asking to
  // change something only makes sense once there's a real mockup to react to (matches "if a
  // customer requests edits it moves back from ready to in progress", which only makes sense
  // starting *from* ready); invoiced/shipped have nothing left to change.
  //
  // Design line items stack in one column, Request Changes underneath all of them — each
  // design gets its own row: a small mockup thumbnail, the product name, and its quantity —
  // no SKU code, no variant/color shown (a mockup is per-design, not per-color, so showing
  // color here would misleadingly imply one mockup per color ordered). Sourced from
  // quote.design_line_items, a customer-safe resolution of sku_print_assignments built
  // server-side by get_quote_endpoint() — only rows that actually have a mockup assigned
  // show up here at all.
  function renderArtworkArea(quote) {
    var status = quote.status;
    var isPaid = status === 'invoiced' || status === 'shipped';
    var phase = getArtworkPhase(quote);
    var designItems = quote.design_line_items || [];

    // Shared upload widget (2026-08-17, factored out so 'in_progress' can offer it too, not
    // just 'upload') — same ids/markup either way, so setupPortalListeners' one #cq-artwork-
    // upload-btn handler keeps working unchanged. upload_quote_artwork() (routes/public.py)
    // already appends onto the quote's existing artwork_urls rather than replacing them, and
    // already allows this for both 'submitted' and 'in_review' status (i.e. both the
    // 'upload' and 'in_progress' phases) — this was purely a missing frontend affordance,
    // not a backend gap.
    function artworkUploadFormHtml(promptText) {
      return [
        '<div class="cq-artwork-upload">',
        '  <p class="cq-sub" style="margin-top:0">' + promptText + '</p>',
        '  <div id="cq-artwork-error" class="cq-alert cq-alert-error" style="display:none"></div>',
        '  <input type="file" id="cq-artwork-input" multiple accept="image/*,.pdf,.ai,.eps">',
        '  <textarea id="cq-artwork-notes" placeholder="Anything about colors, placement, or the design we should know? (optional)" rows="3" style="margin-top:10px"></textarea>',
        '  <button class="cq-btn cq-btn-primary" id="cq-artwork-upload-btn" style="margin-top:10px">Upload</button>',
        '</div>',
      ].join('\n');
    }

    var bodySection;
    if (phase === 'upload') {
      // A paid order with no artwork on file at all (Create Invoice, which skips the whole
      // mockup cycle) has nothing left to upload for — no prompt, just an empty card body,
      // rather than inviting a file upload for an order that's already been paid.
      bodySection = isPaid ? '' : artworkUploadFormHtml(
        'Upload your logo or design file below and we\'ll get started on your mockups.'
      );
    } else if (phase === 'in_progress') {
      // Lets a customer keep sending files while their mockups are still being made —
      // "once a customer submits artwork, allow them to submit more files as needed"
      // (2026-08-17) — previously this phase showed only a static status line with no way
      // to attach anything further until a mockup existed (the 'ready' phase's Request
      // Changes attachment). Still gated off entirely once paid, same as 'upload' above.
      bodySection = '<p class="cq-sub" style="margin-top:0">Your artwork is in — we\'re working on your custom mockups now. We\'ll email you when they\'re ready to review.</p>' +
        (isPaid ? '' : artworkUploadFormHtml('Need to send another file? Upload it below and we\'ll add it to your order.'));
    } else {
      // ready
      bodySection = '<div class="cq-design-list">' + designItems.map(function (item) {
        return '<div class="cq-design-item">' +
          '<a href="' + escHtml(item.mockup_url) + '" target="_blank" class="cq-design-thumb-link">' +
          '<img src="' + escHtml(item.mockup_url) + '" alt="Mockup" loading="lazy" class="cq-design-thumb"></a>' +
          '<div class="cq-design-info">' +
          '<div class="cq-design-title">' + escHtml(item.title) + '</div>' +
          '<div class="cq-design-qty">Qty: ' + (item.qty != null ? item.qty : '') + '</div>' +
          '</div></div>';
      }).join('') + '</div>';
    }

    var editSection = '';
    if (phase === 'ready' && !isPaid) {
      // File upload (2026-07-23) — a reference image/file to go with the description, same
      // accept list as the artwork upload above. Optional: submitting with no file attached
      // still works exactly as before (message-only).
      var editFormHtml = [
        '<p class="cq-edit-label">Need changes?</p>',
        '<textarea id="cq-edit-msg" placeholder="Describe what you\'d like adjusted..." rows="3"></textarea>',
        '<input type="file" id="cq-edit-files" multiple accept="image/*,.pdf,.ai,.eps">',
        '<button class="cq-btn cq-btn-sm" id="cq-edit-btn">Request Changes</button>',
      ].join('\n');
      editSection = '<div class="cq-edit-section">' + editFormHtml + '</div>';
    }

    // Design Notes (2026-08-03) — an optional message staff type into "Customer Notes" on
    // the admin quote detail page. Only rendered when actually entered.
    var notesSection = quote.customer_notes ? [
      '<div class="cq-design-notes">',
      '  <p class="cq-design-notes-label">Design Notes</p>',
      '  <p class="cq-design-notes-text">' + escHtml(quote.customer_notes) + '</p>',
      '</div>',
    ].join('\n') : '';

    return [
      '<div class="cq-card">',
      '  <p class="cq-price-heading">Artwork &amp; Mockups</p>',
      notesSection,
      bodySection,
      editSection,
      '</div>',
    ].join('\n');
  }

  // Permanent "Products & Pricing" card — the same live pricing preview shown at every
  // status now (build_pricing_summary() is computed unconditionally in
  // routes/public.py:get_quote_endpoint()), not just pre-approval — "it should always show
  // the product and pricing" (2026-08-04). The Qty column of this same table becomes an
  // editable input (renderPricingBlock's `editableQty` param) instead of a separate "Confirm
  // Your Quantities" list above it (removed 2026-07-20 — showing the same products twice,
  // once to enter a quantity and again in the priced breakdown, was confusing). Feeds
  // Approve and Complete Order (actions area below), which submits whatever's currently in
  // these inputs.
  function renderSkuPricingArea(quote) {
    // Editable at any not-yet-paid status (2026-08-04, widened from mockup_ready/approved
    // only — "once a qty is in the fields, the invoice should be ready regardless of the
    // artwork details") — checkout no longer waits on mockups at all; the moment staff have
    // attached real SKUs (which happens before mockups exist in practice, once a submitted
    // product group gets resolved to specific catalog SKUs — see the Product SKU Mapping
    // section in CLAUDE.md) a customer can fill in quantities and check out. Once actually
    // paid (invoiced/shipped) there's nothing left to edit.
    var editableQty = quote.status !== 'invoiced' && quote.status !== 'shipped';
    var pricingHtml = renderPricingBlock(quote.pricing, editableQty);
    if (pricingHtml) {
      return renderDeliveryNote(quote.pricing) + pricingHtml;
    }
    // Nothing priced yet at all (no SKUs attached, or attached SKUs no longer resolve to a
    // catalog price, and no fallback quantity known either) — still show the card so its
    // position on the page never disappears. Rare edge case: if there ARE resolved SKUs
    // despite no pricing (e.g. one was deleted from the catalog), still offer a quantity
    // input for each so there's not simply no way to set them.
    var qtyRowsHtml = '';
    if (editableQty && quote.resolved_skus && quote.resolved_skus.length) {
      var rows = quote.resolved_skus.map(function (s) {
        var label = s.color || s.title || s.sku;
        var sub = s.color && s.title ? s.title : s.sku;
        var img = s.image_url
          ? '<img src="' + escHtml(s.image_url) + '" alt="">'
          : '<div class="cq-qty-noimg"></div>';
        return [
          '<div class="cq-qty-row">',
          img,
          '<div class="cq-qty-info"><div class="cq-qty-label">' + escHtml(label) + '</div>',
          '<div class="cq-qty-sub">' + escHtml(sub) + '</div></div>',
          '<input type="text" inputmode="numeric" pattern="[0-9]*" class="cq-qty-input" value="' + (s.quantity || '') + '" data-sku="' + escHtml(s.sku) + '">',
          '</div>',
        ].join('');
      }).join('');
      qtyRowsHtml = '<div class="cq-qty-list">' + rows + '</div>';
    }
    return [
      '<div class="cq-card">',
      '  <p class="cq-price-heading">Products &amp; Pricing</p>',
      '  <p class="cq-sub" style="margin-top:0">' + (
        (quote.product_group_names && quote.product_group_names.length)
          ? quote.product_group_names.map(function (n) { return escHtml(n); }).join('<br>')
          : escHtml(quote.product_name || 'We\'ll add product and pricing details here once they\'re selected.')
      ) + '</p>',
      qtyRowsHtml,
      '</div>',
    ].join('\n');
  }

  // The one checkout action below the two permanent cards (2026-08-04 rework, "make sure it
  // always says [Approve and Complete Order] regardless of the phase") — one button, one
  // label, everywhere it appears, entirely independent of artwork phase: available any time
  // the order hasn't been paid yet, disabled until every qty field is filled in (see
  // setupPortalListeners' updateCheckoutButtonState()). Once actually paid (invoiced/
  // shipped), no button and no invoice link at all ("still show the mockup and price
  // details, just don't link to an invoice") — nothing left to do here.
  //
  // submitCheckout() (below) is the one handler for this button regardless of which quote
  // status it's clicked from — it branches on whether a real invoice already exists
  // (quote.shopify_invoice_url) to decide whether to create one (POST /approve, now allowed
  // from submitted/in_review/mockup_ready — see approve_quote() in routes/public.py) or just
  // update quantities on the existing one (POST /update-quantities), so there's no longer a
  // separate code path per status the way Proceed to Checkout vs Complete My Order used to be.
  function renderActionsArea(quote) {
    var status = quote.status;
    if (status === 'invoiced' || status === 'shipped') return '';
    // Button + note share one shrink-to-fit wrapper (not the note as a separate full-width
    // paragraph) so the note sits directly under the button itself, not centered across the
    // whole card regardless of how wide the button is.
    return [
      '<div class="cq-checkout-wrap">',
      '  <button class="cq-btn cq-btn-primary cq-btn-large" id="cq-checkout-btn">Approve and Complete Order →</button>',
      '  <p class="cq-checkout-note">Fill out the qty fields to proceed.</p>',
      '</div>',
    ].join('\n');
  }

  // $1,241.97 — comma thousands separator, always 2 decimals. Every dollar figure in the
  // pricing block goes through this so nothing over $999 ever renders without a comma.
  function fmtMoney(amount) {
    return '$' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function priceCellHtml(unitPrice, discountedUnit, qty, total) {
    var unitLine = fmtMoney(discountedUnit) + ' each';
    if (Math.round(unitPrice * 100) !== Math.round(discountedUnit * 100)) {
      unitLine = '<span class="cq-price-strike">' + fmtMoney(unitPrice) + '</span> ' + fmtMoney(discountedUnit) + ' each';
    }
    var html = '<div>' + unitLine + '</div>';
    if (qty && total != null) {
      html += '<div class="cq-price-line-total">' + fmtMoney(total) + ' total</div>';
    }
    return html;
  }

  // Wholesale layout's Your Price column — struck-through retail price to the left of the
  // real (base-wholesale) price, same "normal" markdown style the retail layout uses, just
  // without the "each"/"total" wording (the line's actual dollar total lives in its own
  // Total column instead, so there's nothing to disambiguate here).
  function priceCellWholesaleHtml(retailPrice, yourPrice) {
    if (Math.round(retailPrice * 100) !== Math.round(yourPrice * 100)) {
      return '<span class="cq-price-strike">' + fmtMoney(retailPrice) + '</span> ' + fmtMoney(yourPrice);
    }
    return fmtMoney(yourPrice);
  }

  // Delivery estimate — shown above the Pricing block (or on its own if no SKUs are
  // attached yet). Scales with order size once a firm quantity is known
  // (pricing.has_full_pricing): under 500 units ships in the normal 3 days, 500+ takes
  // longer to produce (4–6 days), and 2,000+ needs to be called out explicitly (7+ days).
  function renderDeliveryNote(pricing) {
    var qty = (pricing && pricing.has_full_pricing) ? pricing.total_qty : 0;
    var note = 'Most orders ship within 3 business days.';
    if (qty >= 2000) note = 'Orders of this size ship within 7+ business days.';
    else if (qty >= 500) note = 'Most orders ship within 4–6 business days.';
    return '<div class="cq-delivery-note"><strong>Delivery:</strong> ' + note + '</div>';
  }

  function renderPricingBlock(pricing, editableQty) {
    if (!pricing) return '';

    var wholesale = pricing.wholesale;
    // The Total column only carries real information once every SKU has a firm quantity —
    // without one, every row (and Shipping) would just show a dash in that column, which
    // read as a messy, mostly-empty column. So the wholesale table drops to 3 columns
    // (Product/Qty/Your Price — same shape/widths as the retail table, via the plain
    // cq-price-table class instead of cq-price-table-wholesale) whenever quantity isn't
    // fully known yet, and only grows the 4th Total column once it is.
    var showTotalCol = wholesale && pricing.has_full_pricing;

    // Product name as the primary line, color/finish as a smaller subtitle underneath —
    // a bare color name on its own ("Hot Pink") doesn't say what product it's a color of.
    // Falls back to just the title (no subtitle) when there's no distinct color to show.
    // extraInline (the SKU code, retail only) goes right after the title, before the
    // subtitle — so it reads "Title (SKU)" on one line, "Color" on the next, not SKU
    // trailing after the subtitle. A small product photo (same cached_image_url the admin
    // SKU picker shows) sits to the left, so the customer sees what they're ordering, not
    // just a title/color name — a blank placeholder box takes its place when there's none.
    function productLabelHtml(line, extraInline) {
      var mainText = escHtml(line.title || line.color || line.sku) + (extraInline || '');
      // Links to the real product page on thestainlessdepot.com (2026-07-21,
      // product_skus.cached_product_url) when known — same link the Generate Cost Sheet
      // PDF already shows staff. A SKU that hasn't been added/refreshed since that column
      // existed just has no link, not a broken one.
      var main = line.product_url
        ? '<a href="' + escHtml(line.product_url) + '" target="_blank" rel="noopener" class="cq-price-link">' + mainText + '</a>'
        : mainText;
      var sub = (line.title && line.color) ? '<span class="cq-price-sub">' + escHtml(line.color) + '</span>' : '';
      var img = line.image_url
        ? '<img src="' + escHtml(line.image_url) + '" alt="" class="cq-price-thumb" loading="lazy">'
        : '<div class="cq-price-thumb cq-price-thumb-empty"></div>';
      return '<div class="cq-price-product-inner">' + img + '<div>' + main + sub + '</div></div>';
    }

    // At mockup_ready (editableQty), the Qty cell IS the "Confirm Your Quantities" control —
    // no separate list above this table anymore (2026-07-20 — showing the same products
    // twice, once to enter a quantity and again in the priced breakdown, was confusing).
    // Every other status keeps the plain read-only number/dash.
    function qtyCellHtml(line) {
      if (editableQty) {
        return '<input type="text" inputmode="numeric" pattern="[0-9]*" class="cq-qty-input" data-sku="' + escHtml(line.sku) + '" value="' + (line.quantity || '') + '">';
      }
      return line.quantity || '—';
    }

    var rows = pricing.lines.map(function (line) {
      if (wholesale && showTotalCol) {
        // No SKU code shown — customer-facing, and the code is meaningless to them.
        return '<tr>' +
          '<td class="cq-price-product">' + productLabelHtml(line) + '</td>' +
          '<td class="cq-price-qty">' + qtyCellHtml(line) + '</td>' +
          '<td class="cq-price-amount">' + priceCellWholesaleHtml(line.unit_price, line.wholesale_base_unit_price) + '</td>' +
          '<td class="cq-price-total">' + (line.wholesale_base_line_total != null ? fmtMoney(line.wholesale_base_line_total) : '—') + '</td>' +
          '</tr>';
      }
      if (wholesale) {
        return '<tr>' +
          '<td class="cq-price-product">' + productLabelHtml(line) + '</td>' +
          '<td class="cq-price-qty">' + qtyCellHtml(line) + '</td>' +
          '<td class="cq-price-amount">' + priceCellWholesaleHtml(line.unit_price, line.wholesale_base_unit_price) + '</td>' +
          '</tr>';
      }
      // Retail per-line markdown is base-percent-only (Product Discount alone) now, not
      // discounted_unit_price/line_total (real bug caught live 2026-08-04, "make sure these
      // two discounts work separately all the way through — product discounts work on the
      // line items, bulk discounts work at the order level") — discounted_unit_price/
      // line_total bake any Order Discount percent in too, which meant a retail order with
      // bulk pricing showed one combined per-line price with no separate breakdown at all —
      // same fix as the wholesale-only bulkDiscountRow getting a retail counterpart below.
      return '<tr>' +
        '<td class="cq-price-product">' + productLabelHtml(line, ' <span class="cq-price-sku">(' + escHtml(line.sku) + ')</span>') + '</td>' +
        '<td class="cq-price-qty">' + qtyCellHtml(line) + '</td>' +
        '<td class="cq-price-amount">' + priceCellHtml(line.unit_price, line.wholesale_base_unit_price, line.quantity, line.wholesale_base_line_total) + '</td>' +
        '</tr>';
    }).join('');

    var savingsLine = '';
    if (pricing.has_full_pricing && pricing.total_savings) {
      savingsLine = '<div class="cq-price-savings">You saved ' + fmtMoney(pricing.total_savings) + '</div>';
    }

    // Customization is billed per category (2026-07-21) — one row per category actually
    // present on this quote (pricing.customization_lines), not a single flat rate for the
    // whole order. Each row's *unit* price is always knowable regardless of whether every
    // attached SKU has a firm quantity yet — shown even when has_full_pricing is false, same
    // graceful-degradation rule this row always followed; only qty/total stay conditional.
    // Column shape (3 vs 4) still follows showTotalCol so every row lines up with the
    // product rows above it. The common single-category case still renders exactly one row
    // labeled plain "Customization", unchanged from before per-category pricing existed —
    // only a genuinely mixed order gets more than one.
    var customizationRow = pricing.customization_lines.map(function (cline) {
      var qtyCell = (pricing.has_full_pricing && cline.units != null) ? cline.units : '—';
      if (wholesale && showTotalCol) {
        var totalCell = (pricing.has_full_pricing && cline.total != null) ? fmtMoney(cline.total) : '—';
        return '<tr class="cq-price-item-row"><td class="cq-price-product">' + escHtml(cline.label) + '</td>' +
          '<td class="cq-price-qty">' + qtyCell + '</td>' +
          '<td class="cq-price-amount">' + priceCellWholesaleHtml(cline.fee_per_unit, cline.base_unit_price) + '</td>' +
          '<td class="cq-price-total">' + totalCell + '</td></tr>';
      }
      if (wholesale) {
        return '<tr class="cq-price-item-row"><td class="cq-price-product">' + escHtml(cline.label) + '</td>' +
          '<td class="cq-price-qty">' + qtyCell + '</td>' +
          '<td class="cq-price-amount">' + priceCellWholesaleHtml(cline.fee_per_unit, cline.base_unit_price) + '</td></tr>';
      }
      // Not colspan'd — Customization gets the same Product/Qty/Price shape as a real
      // line, not merged into the product column. base_unit_price/base_total, not
      // discounted_unit_price/total — same Product-Discount-only reasoning as the retail
      // product rows above.
      return '<tr class="cq-price-item-row"><td class="cq-price-product">' + escHtml(cline.label) + '</td>' +
        '<td class="cq-price-qty">' + qtyCell + '</td>' +
        '<td class="cq-price-amount">' + priceCellHtml(
          cline.fee_per_unit, cline.base_unit_price,
          pricing.has_full_pricing ? cline.units : null, pricing.has_full_pricing ? cline.base_total : null
        ) + '</td></tr>';
    }).join('');

    // Bulk Discount's fixed-$ form (quotes.additional_discount_type/_value — renamed
    // 2026-07-23 from "Additional Discount") — a manual override that REPLACES the
    // automatic volume-tier bump with a flat dollar amount instead (the percent form
    // replaces it with a percent instead, already folded into bulkDiscountRow below, so
    // this row and that one are mutually exclusive — never both at once).
    var additionalDiscountRow = '';
    if (pricing.has_full_pricing && pricing.additional_discount_amount) {
      var discountLabel = pricing.bulk_discount_label || 'Order Discount';
      additionalDiscountRow = (wholesale
        ? '<tr class="cq-price-item-row"><td colspan="3" class="cq-price-bulk-label">' + discountLabel + '</td>' +
          '<td class="cq-price-total cq-price-bulk-amount">-' + fmtMoney(pricing.additional_discount_amount) + '</td></tr>'
        : '<tr class="cq-price-item-row"><td colspan="2" class="cq-price-bulk-label">' + discountLabel + '</td>' +
          '<td class="cq-price-amount cq-price-bulk-amount">-' + fmtMoney(pricing.additional_discount_amount) + '</td></tr>');
    }

    var bulkDiscountRow = '';
    var totalRow = '';
    if (pricing.has_full_pricing) {
      // A further bulk-tier bump (+5% at 100 units, +5% more at 500 — on top of the flat
      // Product Discount base already baked into every row above) shown as its own
      // deduction, right above the Total bar, rather than silently folded into the per-line
      // price — that's the only way it's actually visible that it applied. Renamed
      // 2026-07-23 "Bulk Order Discount" → "Bulk Discount", renamed again 2026-08-04 →
      // "Order Discount" ("rename bulk discounts to Order discount") — covers both the
      // untouched-automatic tier and a percent-type manual override of it. **Retail got this
      // row for the first time 2026-08-04** (real bug caught live, "make sure these two
      // discounts work separately all the way through") — see the retail product-row fix
      // above for why it was missing before.
      // Gated on the real amount, not bulk_discount_percent alone (2026-08-04) — see
      // services/email_service.py's mirror of this same gate for why.
      if (pricing.bulk_discount_amount) {
        var bulkLabel = pricing.bulk_discount_label || 'Order Discount';
        bulkDiscountRow = wholesale
          ? '<tr class="cq-price-item-row"><td colspan="3" class="cq-price-bulk-label">' + bulkLabel + ' ' +
            '<span class="cq-price-sku">(' + pricing.total_qty + ' units)</span></td>' +
            '<td class="cq-price-total cq-price-bulk-amount">-' + fmtMoney(pricing.bulk_discount_amount) + '</td></tr>'
          : '<tr class="cq-price-item-row"><td colspan="2" class="cq-price-bulk-label">' + bulkLabel + ' ' +
            '<span class="cq-price-sku">(' + pricing.total_qty + ' units)</span></td>' +
            '<td class="cq-price-amount cq-price-bulk-amount">-' + fmtMoney(pricing.bulk_discount_amount) + '</td></tr>';
      }
      if (wholesale) {
        totalRow = '<tr class="cq-price-total-row"><td colspan="3">Total</td>' +
          '<td class="cq-price-total">' + fmtMoney(pricing.final_total) + savingsLine + '</td></tr>';
      } else {
        totalRow = '<tr class="cq-price-total-row"><td colspan="2">Total</td><td class="cq-price-amount">' + fmtMoney(pricing.final_total) + savingsLine + '</td></tr>';
      }
    }

    // Shipping is its own line item too, same shape (not merged into the product column) —
    // the free-shipping cutoff always shows in the label (it only depends on wholesale
    // status, not on knowing full quantities), but whether *this* order clears it needs a
    // firm subtotal. No per-unit markdown here — shipping isn't discounted, it's just free
    // or not.
    //
    // Manual shipping override (2026-07-16): when set, a plain real charge — no
    // "(free over $X)" qualifier, no FREE/Calculated-at-checkout language at all, just
    // "Shipping" and the literal dollar amount, not styled as FREE (staff took manual
    // control of this line specifically).
    var shippingLabel, shippingPrice;
    if (pricing.manual_shipping_enabled) {
      shippingLabel = 'Shipping';
      shippingPrice = fmtMoney(pricing.manual_shipping_amount);
    } else {
      shippingLabel = 'Shipping <span class="cq-price-sku">(free over $' + pricing.free_shipping_threshold + ')</span>';
      shippingPrice = (pricing.has_full_pricing && pricing.qualifies_for_free_shipping)
        ? '<span class="cq-shipping-free">FREE</span>'
        : '<span class="cq-shipping-tbd">Calculated at checkout</span>';
    }
    var shippingRow = (wholesale && showTotalCol)
      ? '<tr class="cq-price-item-row cq-price-shipping-row"><td class="cq-price-product">' + shippingLabel +
        '</td><td class="cq-price-qty">—</td><td class="cq-price-amount">—</td><td class="cq-price-total">' + shippingPrice + '</td></tr>'
      : '<tr class="cq-price-item-row cq-price-shipping-row"><td class="cq-price-product">' + shippingLabel +
        '</td><td class="cq-price-qty">—</td><td class="cq-price-amount">' + shippingPrice + '</td></tr>';

    // Product Discount's fixed-$ mode no longer gets its own separate deduction row
    // (2026-08-04, was added 2026-08-03) — the flat dollar amount now prorates across every
    // product/customization line (pricing.py) and shows as that line's own struck-through
    // markdown, same as percent mode already did (mirrors email_service.py's own removal of
    // this row, see its comment for why).
    var productDiscountRow = '';

    var discountNote = '';
    // Includes the fixed-$/line-items Product Discount modes (2026-08-03) — both zero
    // discount_percent itself, so that alone would miss them here (same fix as
    // _pricing_html() in services/email_service.py).
    if (pricing.discount_percent || pricing.product_discount_amount || pricing.line_items_mode) {
      // Custom Discount (2026-07-23) — a retail order where staff manually set Product
      // Discount (base_percent's override, or one of its fixed-$/line-items modes) reads
      // as a deliberate one-off, not the regular qty-triggered volume discount. Wholesale
      // orders keep "Wholesale Pricing Applied" regardless of a base override — that's
      // still the wholesale rate, just adjusted.
      var kind;
      var hasManualProductDiscount = pricing.manual_discount_percent != null || pricing.manual_discount_type;
      if (!wholesale && hasManualProductDiscount) {
        kind = 'Custom Discount';
      } else {
        // Retail's automatic case says "Bulk Pricing Discount" (2026-08-04, renamed from
        // "Volume Discount" to match bulkDiscountRow's own label — see pricing.py's
        // display_base_percent, which moved retail's automatic base-percent effect out of
        // the line items and into that row).
        kind = wholesale ? 'Wholesale Pricing' : 'Bulk Pricing Discount';
      }
      discountNote = '<p class="cq-price-note">' + kind + ' Applied</p>';
    }

    // "Wholesale Price" (2026-08-04, "for wholesale orders, the line items should say
    // wholesale pricing" — was "Your Price"), mirrors email_service.py's _pricing_html().
    var theadRow = (wholesale && showTotalCol)
      ? '<tr><th>Product</th><th class="cq-price-qty">Qty</th><th class="cq-price-amount">Wholesale Price</th><th class="cq-price-total">Total</th></tr>'
      : wholesale
        ? '<tr><th>Product</th><th class="cq-price-qty">Qty</th><th class="cq-price-amount">Wholesale Price</th></tr>'
        : '<tr><th>Product</th><th class="cq-price-qty">Qty</th><th class="cq-price-amount">Price</th></tr>';

    return [
      '<div class="cq-card cq-price-card">',
      '  <p class="cq-price-heading">Products &amp; Pricing</p>',
      '  <table class="cq-price-table' + (showTotalCol ? ' cq-price-table-wholesale' : '') + '">',
      '    <thead>' + theadRow + '</thead>',
      '    <tbody>' + rows + customizationRow + shippingRow + bulkDiscountRow + productDiscountRow + additionalDiscountRow + totalRow + '</tbody>',
      '  </table>',
      discountNote,
      '</div>',
    ].join('\n');
  }

  function doApprove(quote, skuUpdates, errorEl, btn, busyText) {
    if (errorEl) errorEl.style.display = 'none';
    if (btn) { btn.disabled = true; btn.textContent = busyText; }
    var opts = { method: 'POST' };
    if (skuUpdates) {
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify({ skus: skuUpdates });
    }
    fetch(API_URL + '/api/quotes/' + encodeURIComponent(quote.id) + '/approve', opts)
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.error || 'Approval failed'); });
        return r.json();
      })
      .then(function (data) {
        if (data.invoice_url) {
          window.location.href = data.invoice_url;
        } else {
          loadPortal(quote.id);
        }
      })
      .catch(function (err) {
        if (errorEl) showError(errorEl, err.message);
        if (btn) { btn.disabled = false; btn.textContent = btn.dataset.restoreText || 'Approve and Complete Order →'; }
      });
  }

  function renderCancelled() {
    return [
      '<div class="cq-status-badge cq-status-cancelled">Cancelled</div>',
      '<h2 class="cq-heading">This quote has been cancelled</h2>',
      '<p class="cq-sub">If you think this is a mistake or want to start a new request, please <a href="/pages/custom-quote">click here</a> or email us at <a href="mailto:custom@customhoggtumblers.com">custom@customhoggtumblers.com</a>.</p>',
    ].join('\n');
  }

  // One checkout button, one handler, regardless of which not-yet-paid status it's clicked
  // from (2026-08-04 rework — previously two separate paths: submitApproval() at
  // mockup_ready via doApprove()/POST /approve, submitCompleteOrder() at approved via POST
  // /update-quantities). Branches on quote.shopify_invoice_url — the public-safe proxy for
  // "does a real Shopify draft order already exist" (the actual shopify_draft_order_id field
  // is stripped from public responses, see models.py:_parse_row()) — not `quote.status`:
  // no invoice yet means this is the *first* approval (now allowed from submitted/in_review/
  // mockup_ready, not just mockup_ready — see approve_quote() in routes/public.py), so it
  // creates one; an existing invoice means this is a later quantity edit against an
  // already-approved order, so it just updates in place. No SKUs on the page at all (edge
  // case — shouldn't happen since staff attach SKUs before a customer can reach this button
  // in practice) submits with no skuUpdates either way, matching create_draft_order()'s own
  // quote.quantity fallback.
  function submitCheckout(quote, btn, errorEl) {
    var inputs = document.querySelectorAll('.cq-qty-input');
    var skuUpdates = [];
    var valid = true;
    var tooLarge = false;
    var anyChanged = false;
    inputs.forEach(function (inp) {
      var qty = parseInt(inp.value, 10);
      if (!qty || qty < 1) valid = false;
      if (qty > 50000) tooLarge = true;
      if (inp.value !== inp.defaultValue) anyChanged = true;
      skuUpdates.push({ sku: inp.dataset.sku, quantity: qty });
    });
    if (inputs.length && !valid) {
      if (errorEl) showError(errorEl, 'Enter a quantity of at least 1 for each item.');
      return;
    }
    if (tooLarge) {
      if (errorEl) showError(errorEl, 'Each quantity must be 50,000 or less — email us for anything larger.');
      return;
    }
    if (errorEl) errorEl.style.display = 'none';

    if (!quote.shopify_invoice_url) {
      btn.dataset.restoreText = btn.textContent;
      doApprove(quote, inputs.length ? skuUpdates : null, errorEl, btn, 'Submitting…');
      return;
    }

    // Already approved once — dirty-check via defaultValue (same guard the old standalone
    // Update Quantities button used) so an unchanged quantity skips straight to the existing
    // invoice instead of a needless regenerate-then-redirect round trip.
    if (!inputs.length || !anyChanged) {
      window.location.href = quote.shopify_invoice_url;
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Updating your invoice…';
    fetch(API_URL + '/api/quotes/' + encodeURIComponent(quote.id) + '/update-quantities', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skus: skuUpdates }),
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.error || 'Update failed'); });
        return r.json();
      })
      .then(function (data) {
        if (data.invoice_url) {
          window.location.href = data.invoice_url;
        } else {
          loadPortal(quote.id);
        }
      })
      .catch(function (err) {
        if (errorEl) showError(errorEl, err.message);
        btn.disabled = false;
        btn.textContent = 'Approve and Complete Order →';
      });
  }

  // Starts disabled until every qty field on the page has a real quantity (≥ 1) —
  // recomputed on every keystroke across all of them. No qty inputs on the page at all
  // (nothing attached, or every SKU already quantity-set some other way) means there's
  // nothing to block on, so the button stays enabled.
  function updateCheckoutButtonState() {
    var allValid = true;
    document.querySelectorAll('.cq-qty-input').forEach(function (inp) {
      var qty = parseInt(inp.value, 10);
      if (!qty || qty < 1) allValid = false;
    });
    var btn = document.getElementById('cq-checkout-btn');
    if (btn) btn.disabled = !allValid;
  }

  function setupPortalListeners(quote) {
    var checkoutBtn = document.getElementById('cq-checkout-btn');
    var editBtn = document.getElementById('cq-edit-btn');
    var errorEl = document.getElementById('cq-portal-error');

    var artworkBtn = document.getElementById('cq-artwork-upload-btn');
    if (artworkBtn) {
      artworkBtn.addEventListener('click', function () {
        var input = document.getElementById('cq-artwork-input');
        var notesInput = document.getElementById('cq-artwork-notes');
        var artworkErrorEl = document.getElementById('cq-artwork-error');
        if (!input.files.length) { showError(artworkErrorEl, 'Choose at least one file.'); return; }
        var fd = new FormData();
        for (var i = 0; i < input.files.length; i++) fd.append('files[]', input.files[i]);
        if (notesInput && notesInput.value.trim()) fd.append('customization_notes', notesInput.value.trim());
        artworkBtn.disabled = true;
        artworkBtn.textContent = 'Uploading…';
        fetch(API_URL + '/api/quotes/' + encodeURIComponent(quote.id) + '/artwork', { method: 'POST', body: fd })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (d) { throw new Error(d.error || 'Upload failed'); });
            return r.json();
          })
          .then(function () { loadPortal(quote.id); })
          .catch(function (err) {
            showError(artworkErrorEl, err.message);
            artworkBtn.disabled = false;
            artworkBtn.textContent = 'Upload';
          });
      });
    }

    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', function () {
        submitCheckout(quote, checkoutBtn, errorEl);
      });
    }
    document.querySelectorAll('.cq-qty-input').forEach(function (inp) {
      inp.addEventListener('input', updateCheckoutButtonState);
    });
    updateCheckoutButtonState();

    if (editBtn) {
      editBtn.addEventListener('click', function () {
        var msg = (document.getElementById('cq-edit-msg').value || '').trim();
        if (!msg) { if (errorEl) showError(errorEl, 'Please describe the changes you need.'); return; }
        if (errorEl) errorEl.style.display = 'none';
        editBtn.disabled = true;
        editBtn.textContent = 'Sending…';
        // FormData (2026-07-23, was JSON) so an optional attached file can ride along —
        // same shape as the artwork-upload handler above. request-edit()'s message parsing
        // already tolerated request.form as a fallback before this, so no server-side
        // change was needed just to accept form data instead of JSON.
        var fd = new FormData();
        fd.append('message', msg);
        var filesInput = document.getElementById('cq-edit-files');
        if (filesInput) {
          for (var i = 0; i < filesInput.files.length; i++) fd.append('files[]', filesInput.files[i]);
        }
        fetch(API_URL + '/api/quotes/' + encodeURIComponent(quote.id) + '/request-edit', {
          method: 'POST',
          body: fd,
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (d) { throw new Error(d.error || 'Failed to send'); });
            return r.json();
          })
          .then(function () {
            container.innerHTML = [
              '<div class="cq-wrap">',
              '  <div class="cq-success-icon">&#10003;</div>',
              '  <h2 class="cq-heading">Edit request sent!</h2>',
              '  <p class="cq-sub">We\'ve received your feedback and will update the mockups shortly. We\'ll email you when the revised mockups are ready.</p>',
              '</div>',
            ].join('\n');
          })
          .catch(function (err) {
            if (errorEl) showError(errorEl, err.message);
            editBtn.disabled = false;
            editBtn.textContent = 'Request Edits';
          });
      });
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  function showError(el, msg) {
    el.textContent = msg;
    el.style.display = 'block';
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Styles ─────────────────────────────────────────────────────────────────

  function injectStyles() {
    if (document.getElementById('cq-styles')) return;

    if (!document.getElementById('cq-fonts')) {
      var l1 = document.createElement('link'); l1.rel = 'preconnect'; l1.href = 'https://fonts.googleapis.com'; document.head.appendChild(l1);
      var l2 = document.createElement('link'); l2.rel = 'preconnect'; l2.href = 'https://fonts.gstatic.com'; l2.crossOrigin = 'anonymous'; document.head.appendChild(l2);
      var l3 = document.createElement('link'); l3.id = 'cq-fonts'; l3.rel = 'stylesheet'; l3.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,400;0,600;0,700;1,400&display=swap'; document.head.appendChild(l3);
    }

    var style = document.createElement('style');
    style.id = 'cq-styles';
    style.textContent = [
      /* Layout */
      '.cq-wrap { max-width: 680px; margin: 0 auto; padding: 32px 0 60px; font-family: "Noto Sans", system-ui, -apple-system, sans-serif; color: #101010; }',
      '.cq-heading { font-size: 26px; font-weight: 700; margin: 0 0 8px; color: #182e49; letter-spacing: -0.01em; }',
      '.cq-sub { font-size: 15px; color: #4a5a6b; margin: 0 0 28px; line-height: 1.6; }',
      '.cq-sub a { color: #182e49; font-weight: 600; }',
      /* The rule now sits on the row, so a section can carry a note beside its label
         without the underline stopping short of the note. */
      '.cq-section-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; border-bottom: 2px solid #182e49; padding-bottom: 6px; margin: 0 0 16px; }',
      '.cq-section-label { font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #182e49; margin: 0; }',
      '.cq-section-note { font-size: 12px; color: #8a9ab0; margin-left: auto; }',
      /* Holds the second column of a one-field row so it lines up with the rows above
         instead of the field stretching or floating at its own width. */
      '.cq-field-blank { margin-bottom: 0; }',
      '.cq-row { display: flex; gap: 16px; }',
      '.cq-field { flex: 1; margin-bottom: 18px; }',
      '.cq-field label { display: block; font-size: 13px; font-weight: 600; color: #182e49; margin-bottom: 6px; letter-spacing: 0.01em; }',
      '.cq-req { color: #dc2626; }',
      '.cq-opt { font-weight: 400; color: #8a9ab0; font-size: 12px; }',
      '.cq-submit-section { margin: 28px 0 12px; text-align: center; }',

      /* Inputs */
      '.cq-field input[type="text"], .cq-field input[type="email"], .cq-field input[type="tel"], .cq-field input[type="number"], .cq-field textarea { width: 100%; padding: 10px 14px; border: 1.5px solid #d0d9e3; border-radius: 6px; font-size: 14px; font-family: inherit; background: white; color: #101010; transition: border-color .15s, box-shadow .15s; box-sizing: border-box; }',
      '.cq-field input:focus, .cq-field textarea:focus { outline: none; border-color: #182e49; box-shadow: 0 0 0 3px rgba(24,46,73,.1); }',
      '.cq-field textarea { resize: vertical; }',
      '.cq-field input[type="file"] { font-size: 13px; padding: 6px 0; }',
      '.cq-file-hint { font-size: 12px; color: #8a9ab0; margin-top: 4px; }',
      '.cq-radio-group { display: flex; gap: 20px; flex-wrap: wrap; }',
      '.cq-radio { display: flex; align-items: center; gap: 6px; font-size: 14px; cursor: pointer; font-weight: 400; }',
      '.cq-radio input { width: auto; margin: 0; accent-color: #182e49; }',

      /* Progress */
      '.cq-prog-bar { height: 4px; background: #d0d9e3; border-radius: 2px; margin-bottom: 28px; overflow: hidden; }',
      '.cq-prog-fill { height: 100%; background: #182e49; border-radius: 2px; transition: width .3s ease; }',

      /* Step footer */
      '.cq-step-footer { display: flex; align-items: center; gap: 12px; margin-top: 24px; flex-wrap: wrap; }',
      '.cq-step-count { font-size: 12px; font-weight: 600; color: #8a9ab0; letter-spacing: .04em; margin-left: auto; }',
      '.cq-sel-count { font-size: 13px; font-weight: 600; color: #182e49; }',

      /* Buttons */
      '.cq-btn { display: inline-block; padding: 12px 28px; border-radius: 6px; font-size: 14px; font-weight: 700; cursor: pointer; border: 2px solid transparent; font-family: inherit; transition: opacity .15s, transform .15s; text-decoration: none; letter-spacing: 0.02em; }',
      '.cq-btn:hover { opacity: .88; transform: translateY(-1px); }',
      '.cq-btn-primary { background: #182e49; color: white; border-color: #182e49; }',
      '.cq-btn-outline { background: white; color: #182e49; border-color: #d0d9e3; }',
      '.cq-btn-outline:hover { background: #edf0f3; }',
      '.cq-btn-sm { padding: 8px 18px; font-size: 13px; background: #edf0f3; color: #182e49; border-color: #d0d9e3; }',
      '.cq-btn-large { padding: 15px 36px; font-size: 16px; margin-top: 8px; }',
      '.cq-btn:disabled { opacity: .5; cursor: not-allowed; transform: none; }',

      /* Alerts */
      '.cq-alert { padding: 11px 15px; border-radius: 6px; font-size: 13px; margin-bottom: 16px; }',
      '.cq-alert-error { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }',
      '.cq-alert a { color: inherit; }',

      /* Category tabs */
      '.cq-cat-tabs { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 20px; }',
      '.cq-cat-tab { padding: 7px 14px; border-radius: 20px; font-size: 13px; font-weight: 600; cursor: pointer; border: 1.5px solid #d0d9e3; background: white; color: #4a5a6b; font-family: inherit; transition: border-color .15s, background .15s, color .15s; }',
      '.cq-cat-tab:hover { border-color: #182e49; color: #182e49; }',
      '.cq-cat-tab.active { background: #182e49; color: white; border-color: #182e49; }',

      /* Image slider (cards) */
      '.cq-slider { position: relative; width: 100%; height: 100%; overflow: hidden; touch-action: pan-y; }',
      '.cq-slider-track { display: flex; height: 100%; transition: transform 0.25s ease; will-change: transform; }',
      '.cq-slider-track img { min-width: 100%; width: 100%; height: 100%; object-fit: cover; display: block; user-select: none; }',
      '.cq-slider-dots { position: absolute; bottom: 6px; left: 0; right: 0; display: flex; justify-content: center; gap: 4px; pointer-events: none; }',
      '.cq-dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(255,255,255,.5); transition: background .15s; }',
      '.cq-dot-active { background: white; box-shadow: 0 1px 3px rgba(0,0,0,.3); }',
      '.cq-slider-arrow { display: none; position: absolute; top: 50%; transform: translateY(-50%); width: 28px; height: 28px; border-radius: 50%; border: none; background: rgba(16,16,16,.45); color: white; font-size: 18px; line-height: 1; cursor: pointer; align-items: center; justify-content: center; padding: 0 0 2px; z-index: 1; transition: background .15s; }',
      '.cq-slider-arrow:hover { background: rgba(16,16,16,.7); }',
      '.cq-slider-arrow-prev { left: 6px; }',
      '.cq-slider-arrow-next { right: 6px; }',
      '@media (hover: hover) and (pointer: fine) { .cq-slider-arrow { display: flex; } }',

      /* Modal slider */
      '.cq-modal-slider { position: relative; overflow: hidden; border-radius: 12px 12px 0 0; touch-action: pan-y; }',
      '.cq-modal-slider-track { display: flex; transition: transform 0.25s ease; }',
      '.cq-modal-slider-track img { min-width: 100%; width: 100%; aspect-ratio: 1/1; object-fit: contain; background: #f5f8fc; display: block; user-select: none; border-radius: 0; }',
      '.cq-modal-dots { position: absolute; bottom: 8px; }',

      /* Product grid */
      '.cq-pg-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 8px; }',
      '.cq-pg-card { border: 2px solid #d0d9e3; border-radius: 10px; overflow: hidden; cursor: pointer; transition: border-color .15s, box-shadow .15s; background: white; }',
      '.cq-pg-card:hover { border-color: #182e49; box-shadow: 0 2px 8px rgba(24,46,73,.1); }',
      '.cq-pg-card.cq-selected { border-color: #182e49; box-shadow: 0 0 0 3px rgba(24,46,73,.12); }',
      '.cq-pg-img-wrap { position: relative; aspect-ratio: 1 / 1; overflow: hidden; background: #f5f8fc; }',
      '.cq-pg-img-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }',
      '.cq-pg-emoji { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 48px; }',
      '.cq-pg-check { position: absolute; top: 8px; right: 8px; width: 28px; height: 28px; background: #182e49; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700; box-shadow: 0 2px 6px rgba(0,0,0,.2); }',
      '.cq-pg-body { padding: 10px 12px 12px; }',
      '.cq-pg-name { font-size: 13px; font-weight: 700; color: #182e49; margin-bottom: 3px; line-height: 1.3; }',
      '.cq-pg-sub { font-size: 11px; color: #4a5a6b; line-height: 1.4; margin-bottom: 6px; }',
      '.cq-pg-info-btn { font-size: 12px; font-weight: 600; color: #182e49; background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; font-family: inherit; }',
      '.cq-pg-info-btn:hover { opacity: .7; }',

      /* Selected summary */
      '.cq-selected-summary { background: #edf0f3; border-radius: 6px; padding: 10px 14px; font-size: 13px; color: #182e49; margin-bottom: 20px; line-height: 1.5; }',

      /* Decoration style cards */
      '.cq-deco-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 8px; }',
      '.cq-deco-card { border: 2px solid #d0d9e3; border-radius: 10px; overflow: hidden; cursor: pointer; transition: border-color .15s, box-shadow .15s; background: white; }',
      '.cq-deco-card:hover { border-color: #182e49; box-shadow: 0 2px 8px rgba(24,46,73,.1); }',
      '.cq-deco-card.cq-selected { border-color: #182e49; box-shadow: 0 0 0 3px rgba(24,46,73,.12); }',
      '.cq-deco-img { width: 100%; aspect-ratio: 4/3; object-fit: cover; display: block; }',
      '.cq-deco-placeholder { width: 100%; aspect-ratio: 4/3; background: #f5f8fc; display: flex; align-items: center; justify-content: center; font-size: 48px; }',
      '.cq-deco-label { font-size: 14px; font-weight: 700; color: #182e49; padding: 10px 12px 4px; }',
      '.cq-deco-desc { font-size: 12px; color: #4a5a6b; padding: 0 12px 12px; line-height: 1.4; }',
      '.cq-no-pref-btn { font-size: 13px; font-weight: 600; color: #8a9ab0; background: none; border: none; cursor: pointer; font-family: inherit; text-decoration: underline; padding: 0; }',
      '.cq-no-pref-btn.active { color: #182e49; }',

      /* Product variant picker (step 3) — one card per real SKU combo (color+size as they
         actually exist in the catalog), grouped under the product title, so a customer can
         only ever pick a combination we actually carry (no more picking "Black" + "30oz"
         independently and landing on a combo, like a black 30oz skinny, that doesn't exist). */
      '.cq-opt-group { border: 1.5px solid #d0d9e3; border-radius: 10px; padding: 14px 16px 16px; margin-bottom: 16px; background: white; }',
      '.cq-opt-group-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }',
      '.cq-opt-group-name { font-size: 15px; font-weight: 700; color: #182e49; }',
      '.cq-opt-count { font-size: 12px; font-weight: 600; color: #8a9ab0; }',
      /* The escape hatch used to be an underlined link buried under a very long wall of
         cards, where a customer who wanted it had to scroll past everything to find it.
         In the header it sits next to the product name, before the wall, as a real chip. */
      '.cq-nopref-chip { margin-left: auto; font-size: 12px; font-weight: 600; font-family: inherit; color: #4a5a6b; background: white; border: 1.5px solid #d0d9e3; border-radius: 999px; padding: 5px 12px; cursor: pointer; transition: border-color .15s, background .15s, color .15s; }',
      '.cq-nopref-chip:hover { border-color: #8a9ab0; }',
      '.cq-nopref-chip.active { background: #182e49; border-color: #182e49; color: white; }',
      '.cq-opt-loading, .cq-opt-none { font-size: 13px; color: #8a9ab0; margin: 0; }',
      '.cq-opt-unavailable { border: 1.5px dashed #d0d9e3; border-radius: 8px; background: #f8fafc; padding: 14px 16px; }',
      '.cq-opt-unavailable-msg { font-size: 13.5px; color: #182e49; margin: 0 0 10px; line-height: 1.5; }',
      '.cq-opt-switch { font-size: 13px; padding: 8px 14px; }',
      '.cq-opt-unavailable-note { font-size: 12px; color: #8a9ab0; margin: 10px 0 0; line-height: 1.5; }',
      '.cq-opt-products { display: flex; flex-direction: column; gap: 10px; }',
      /* "No preference" greys the whole wall out at once rather than each card
         individually — the per-card opacity left the box chrome at full strength, so a
         disabled group still read as active at a glance. */
      '.cq-opt-nopref .cq-opt-products { opacity: .4; pointer-events: none; }',
      /* One box per size (or one box total when the group is single-sized) — size is the
         top-level axis, so every option of a given size is inside one bounded container. */
      '.cq-opt-section { border: 1.5px solid #e3e8ee; border-radius: 8px; background: #f8fafc; padding: 12px; }',
      '.cq-opt-size-head { display: flex; align-items: baseline; gap: 8px; font-size: 14px; font-weight: 700; color: #182e49; margin: 0 0 10px; }',
      '.cq-opt-size-count { font-size: 11px; font-weight: 500; color: #8a9ab0; }',
      '.cq-opt-axis { font-size: 10.5px; font-weight: 700; color: #8a9ab0; text-transform: uppercase; letter-spacing: .06em; }',
      /* A product that comes exactly one way: shown back as a statement, not a control. */
      '.cq-opt-sole-note { font-size: 12.5px; color: #8a9ab0; margin: 0; }',
      '.cq-opt-group-size { font-size: 12px; font-weight: 600; color: #8a9ab0; }',
      /* A product line within a size — a light label plus its grid, not a nested box. */
      '.cq-opt-line + .cq-opt-line { margin-top: 12px; padding-top: 12px; border-top: 1px solid #e3e8ee; }',
      '.cq-opt-line-label { display: flex; align-items: baseline; gap: 8px; font-size: 11.5px; font-weight: 700; color: #4a5a6b; text-transform: uppercase; letter-spacing: .04em; margin: 0 0 8px; }',
      '.cq-opt-line-count { font-size: 11px; font-weight: 500; color: #8a9ab0; text-transform: none; letter-spacing: 0; }',
      /* Grid rather than a wrapping flex row: fixed tracks keep every card the same width
         and every row flush, instead of a ragged right edge that shifted per row. */
      '.cq-opt-variant-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 8px; }',
      /* The one-off styles line: names, not colors, so the cards go wide and horizontal —
         "Sublimatable Holographic Skinny Tumbler" wraps to four lines in a 96px card. */
      '.cq-opt-grid-wide { grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); }',
      '.cq-opt-variant { position: relative; border: 2px solid #d0d9e3; border-radius: 8px; padding: 6px 5px 7px; cursor: pointer; text-align: center; background: white; font-family: inherit; display: flex; flex-direction: column; gap: 5px; transition: border-color .15s, background .15s, box-shadow .15s; }',
      '.cq-opt-variant:hover { border-color: #8a9ab0; }',
      '.cq-opt-variant-img { display: block; width: 100%; aspect-ratio: 1; border-radius: 5px; background: #f2f5f8; overflow: hidden; }',
      '.cq-opt-variant-img img { width: 100%; height: 100%; object-fit: contain; display: block; }',
      '.cq-opt-variant-noimg { display: flex; align-items: center; justify-content: center; color: #b0b8c2; font-size: 22px; }',
      '.cq-opt-variant-label { font-size: 11.5px; font-weight: 700; color: #182e49; line-height: 1.2; }',
      '.cq-opt-wide { flex-direction: row; align-items: center; gap: 9px; text-align: left; padding: 7px 9px; }',
      '.cq-opt-wide .cq-opt-variant-img { flex: 0 0 40px; width: 40px; }',
      '.cq-opt-wide .cq-opt-variant-label { min-width: 0; }',
      '.cq-opt-check { position: absolute; top: 4px; right: 4px; width: 17px; height: 17px; border-radius: 50%; background: #182e49; color: white; font-size: 11px; line-height: 17px; text-align: center; opacity: 0; transform: scale(.6); transition: opacity .12s, transform .12s; }',
      '.cq-opt-variant.cq-selected { border-color: #182e49; background: #eef1f6; box-shadow: 0 0 0 2px rgba(24,46,73,.15); }',
      '.cq-opt-variant.cq-selected .cq-opt-check { opacity: 1; transform: scale(1); }',

      /* Steps already answered, stacked above the current one — the nearest bar is the
         previous page, and any earlier bar is a direct jump back to that step. */
      '.cq-trail { display: flex; flex-direction: column; gap: 6px; margin-bottom: 18px; }',
      '.cq-trail-step { display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; font-family: inherit; background: #f8fafc; border: 1.5px solid #e3e8ee; border-radius: 8px; padding: 9px 12px; cursor: pointer; transition: border-color .15s, background .15s; }',
      '.cq-trail-step:hover { border-color: #8a9ab0; background: #f2f5f8; }',
      '.cq-trail-arrow { font-size: 13px; color: #8a9ab0; }',
      '.cq-trail-name { font-size: 12px; font-weight: 700; color: #182e49; text-transform: uppercase; letter-spacing: .04em; white-space: nowrap; }',
      '.cq-trail-val { flex: 1; min-width: 0; font-size: 12.5px; color: #4a5a6b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }',
      '.cq-trail-edit { font-size: 11.5px; font-weight: 600; color: #8a9ab0; text-decoration: underline; white-space: nowrap; }',
      '.cq-trail-step:hover .cq-trail-edit { color: #182e49; }',

      /* File picker */
      '.cq-file-input { position: absolute; width: 1px; height: 1px; opacity: 0; overflow: hidden; }',
      '.cq-file-drop { display: flex; align-items: center; gap: 10px; border: 1.5px dashed #d0d9e3; border-radius: 8px; padding: 14px 16px; cursor: pointer; font-size: 13px; color: #4a5a6b; background: #f8fafc; transition: border-color .15s, background .15s; }',
      '.cq-file-drop:hover { border-color: #8a9ab0; background: #f2f5f8; }',
      '.cq-file-drop strong { color: #182e49; }',
      '.cq-file-drop-icon { font-size: 18px; }',
      '.cq-file-input:focus-visible + .cq-file-drop { border-color: #182e49; }',
      '.cq-file-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }',
      '.cq-file-pill { font-size: 11.5px; font-weight: 600; color: #4a5a6b; background: #edf0f3; border-radius: 999px; padding: 3px 10px; }',

      /* Submit section */
      '.cq-btn-submit { padding: 15px 40px; font-size: 16px; width: 100%; }',
      '.cq-submit-note { font-size: 13px; color: #4a5a6b; margin-top: 10px; line-height: 1.5; }',

      /* Modal */
      '.cq-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 20px; box-sizing: border-box; }',
      '.cq-modal { background: white; border-radius: 12px; max-width: 400px; width: 100%; max-height: 90vh; overflow-y: auto; position: relative; box-shadow: 0 20px 60px rgba(0,0,0,.3); }',
      '.cq-modal-close { position: absolute; top: 12px; right: 14px; font-size: 24px; background: none; border: none; cursor: pointer; color: #4a5a6b; line-height: 1; font-family: inherit; z-index: 1; }',
      '.cq-modal-close:hover { color: #182e49; }',
      '.cq-modal-img { width: 100%; aspect-ratio: 1/1; object-fit: contain; background: #f5f8fc; display: block; border-radius: 12px 12px 0 0; }',
      '.cq-modal-emoji { width: 100%; aspect-ratio: 1/1; background: #f5f8fc; display: flex; align-items: center; justify-content: center; font-size: 72px; border-radius: 12px 12px 0 0; }',
      '.cq-modal-content { padding: 20px 24px 24px; }',
      '.cq-modal-name { font-size: 20px; font-weight: 700; color: #182e49; margin: 0 0 10px; }',
      '.cq-modal-sub { font-size: 14px; color: #4a5a6b; line-height: 1.5; margin: 0 0 16px; }',
      '.cq-modal-specs { width: 100%; border-collapse: collapse; margin-bottom: 20px; }',
      '.cq-modal-spec-key { font-size: 12px; font-weight: 700; color: #8a9ab0; text-transform: uppercase; letter-spacing: .06em; padding: 6px 12px 6px 0; width: 110px; vertical-align: top; border-bottom: 1px solid #edf0f3; }',
      '.cq-modal-spec-val { font-size: 13px; color: #182e49; padding: 6px 0; border-bottom: 1px solid #edf0f3; }',
      '.cq-modal-swatches { margin-bottom: 20px; }',
      '.cq-modal-color-label { font-size: 12px; font-weight: 700; color: #8a9ab0; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 8px; }',
      '.cq-swatch-row { display: flex; flex-wrap: wrap; gap: 6px; }',
      '.cq-swatch { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; border: 1px solid #d8dee8; cursor: default; }',
      '.cq-modal-toggle { width: 100%; }',
      '.cq-modal-added { background: #f0f9f0; border-color: #059669; color: #059669; }',
      '.cq-modal-added:hover { background: #fee2e2; border-color: #dc2626; color: #dc2626; opacity: 1; }',

      /* Success */
      '.cq-success-icon { font-size: 32px; background: #182e49; color: white; width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 0 20px; }',
      '.cq-card { background: #edf0f3; border: 1px solid #d0d9e3; border-radius: 8px; padding: 18px 22px; margin: 20px 0; }',
      '.cq-copy-row { display: flex; gap: 8px; align-items: center; }',
      '.cq-copy-row input { flex: 1; padding: 8px 10px; border: 1px solid #d0d9e3; border-radius: 5px; font-size: 12px; font-family: monospace; background: white; }',

      /* Pricing preview */
      '.cq-price-heading { margin:0 0 10px; font-size: 11px; color: #182e49; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; }',
      '.cq-price-table { width: 100%; border-collapse: collapse; font-size: 14px; table-layout: fixed; }',
      '.cq-price-table th { text-align: left; padding: 4px 4px 4px 0; border-bottom: 2px solid #d0d9e3; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #8a9ab0; vertical-align: bottom; }',
      // vertical-align: top (not the table default of middle) so rows line up at the first
      // line regardless of how tall any one cell gets — the Product cell can run to 2 lines
      // (long title + SKU code) while Price stays 1 line (no qty) or 2 (qty known, "each" +
      // "total" stacked); without this the shorter cells drift to mid-height of the taller
      // one and the row reads as misaligned.
      '.cq-price-table td { padding: 8px 4px 8px 0; border-bottom: 1px solid #edf0f3; color: #182e49; vertical-align: top; }',
      '.cq-price-table tr:last-child td { border-bottom: none; }',
      // Product gets the bulk of the row — it's the only column with real variable-length
      // text (title + SKU code); Qty and Price are both short, fixed-format content and
      // don't need much room. Product/Qty sit close together (tight shared gap); Price gets
      // its own wider gap on the left so it reads as a visually distinct column.
      '.cq-price-product { width: 58%; }',
      '.cq-price-qty { width: 12%; text-align: center; padding-left: 4px !important; padding-right: 8px !important; white-space: nowrap; }',
      '.cq-price-table th.cq-price-qty { text-align: center; }',
      // Compact override for the editable Qty input embedded directly in the pricing table
      // (mockup_ready) — the column itself is only ~10-12% of the table width, too narrow
      // for the .cq-qty-input's normal 70px everywhere else on the page.
      // Tighter (and top-heavy, not symmetric) padding than the input's normal 8px — a
      // plain <input>'s own border+padding pushes its text down from the top of its box,
      // while the adjacent Your Price/Total cells' text sits flush against the cell's own
      // top padding (both cells use vertical-align:top). Shrinking this gets the input's
      // visible number much closer to that same top edge instead of visibly sitting lower.
      '.cq-price-qty .cq-qty-input { width: 56px; padding: 2px 4px 3px; }',
      '.cq-price-amount { width: 30%; text-align: right; white-space: nowrap; padding-left: 16px !important; }',
      '.cq-price-sku { color: #999; font-size: 12px; }',
      '.cq-price-link { color: #182e49; text-decoration: underline; }',
      '.cq-price-link:hover { color: #f0a955; }',
      '.cq-price-sub { display: block; color: #6b7280; font-size: 12px; margin-top: 1px; }',
      '.cq-price-product-inner { display: flex; align-items: center; gap: 8px; }',
      '.cq-price-thumb, .cq-price-thumb-empty { width: 30px; height: 30px; object-fit: cover; border-radius: 5px; background: #edf0f3; flex-shrink: 0; }',
      '.cq-price-strike { text-decoration: line-through; color: #999; }',
      '.cq-price-line-total { color: #666; font-size: 12px; margin-top: 2px; }',
      '.cq-price-item-row td { border-top: 1px solid #d0d9e3; }',
      '.cq-price-total-row td { font-weight: 700; border-top: 2px solid #182e49; border-bottom: none; padding-top: 10px; }',
      '.cq-price-savings { color: #059669; font-size: 12px; font-weight: 400; margin-top: 2px; }',
      '.cq-shipping-free { color: #059669; font-weight: 700; }',
      '.cq-shipping-tbd { color: #666; font-weight: 400; }',
      '.cq-price-note { color: #059669; font-size: 13px; text-align: right; margin: 8px 0 0; }',
      // Wholesale's Your Price / Total 4-column layout — Your Price shows the normal
      // struck-through-retail-next-to-real-price markdown (reuses .cq-price-amount's
      // styling), Total is its own column for the line's dollar amount.
      '.cq-price-table-wholesale .cq-price-product { width: 38%; }',
      '.cq-price-table-wholesale .cq-price-qty { width: 10%; }',
      '.cq-price-table-wholesale .cq-price-amount { width: 26%; padding-left: 12px !important; font-weight: 600; white-space: nowrap; }',
      '.cq-price-table-wholesale .cq-price-total { width: 26%; text-align: right; white-space: nowrap; padding-left: 12px !important; }',
      '.cq-price-table-wholesale th.cq-price-total { text-align: right; }',
      '.cq-price-bulk-label { color: #059669; }',
      '.cq-price-bulk-amount { color: #059669; }',
      '.cq-delivery-note { color: #666; font-size: 13px; margin: 14px 0 0; padding-top: 12px; border-top: 1px solid #d0d9e3; }',
      '.cq-delivery-note strong { color: #182e49; }',

      /* Status badges */
      '.cq-status-badge { display: inline-block; padding: 5px 14px; border-radius: 20px; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 16px; }',
      '.cq-status-pending { background: #fef3c7; color: #92400e; }',
      '.cq-status-ready { background: #182e49; color: white; }',
      '.cq-status-approved { background: #f0a955; color: white; }',
      '.cq-status-cancelled { background: #edf0f3; color: #6b7280; }',

      /* Design line items (2026-07-25, replaces the old flat .cq-mockup-grid gallery and the
         side-by-side layout with Request Changes — always stacked now, one row per design,
         Request Changes always underneath all of them via .cq-edit-section further down).
         Sized up (2026-07-25 follow-up, from 64px/14px) to actually use the card's width —
         a per-design list item still, not the old 180px full-gallery, but the smaller size
         left too much empty space next to short titles like "Tumbler". */
      '.cq-design-list { display: flex; flex-direction: column; gap: 14px; margin: 16px 0; }',
      '.cq-design-item { display: flex; align-items: center; gap: 16px; }',
      '.cq-design-thumb-link { flex-shrink: 0; }',
      '.cq-design-thumb { width: 100px; height: 100px; object-fit: cover; border-radius: 8px; border: 1px solid #d0d9e3; display: block; }',
      '.cq-design-info { min-width: 0; }',
      '.cq-design-title { font-size: 18px; font-weight: 600; color: #182e49; }',
      '.cq-design-qty { font-size: 14px; color: #6b7280; margin-top: 2px; }',

      /* Design Notes (2026-08-03) — a message from the designer, shown above the mockups
         whenever staff have entered one on the admin side. */
      '.cq-design-notes { background: #f4f7fa; border: 1px solid #d0d9e3; border-radius: 8px; padding: 14px 16px; margin: 16px 0; }',
      '.cq-design-notes-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; margin: 0 0 6px; }',
      '.cq-design-notes-text { font-size: 14px; color: #182e49; margin: 0; white-space: pre-wrap; }',

      /* Portal */
      '.cq-portal-actions { margin: 16px 0; display: flex; gap: 10px; align-items: center; }',
      // Shrinks to the button's own width (inline-flex column, not a full-width block) so
      // the note below lines up directly under the button instead of centering across
      // whatever's wider — the card, the page, etc.
      '.cq-checkout-wrap { margin: 16px 0; display: inline-flex; flex-direction: column; align-items: stretch; }',
      '.cq-checkout-note { color: #6b7280; font-size: 12px; text-align: center; margin: 6px 0 0; }',
      '.cq-qty-list { margin: 16px 0; }',
      '.cq-qty-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid #d0d9e3; }',
      '.cq-qty-row img, .cq-qty-noimg { width: 48px; height: 48px; object-fit: cover; border-radius: 6px; background: #edf0f3; flex-shrink: 0; }',
      '.cq-qty-info { flex: 1; min-width: 0; }',
      '.cq-qty-label { font-size: 14px; font-weight: 600; color: #182e49; }',
      '.cq-qty-sub { font-size: 12px; color: #6b7280; }',
      '.cq-qty-input { width: 76px; padding: 8px 10px; border: 1.5px solid #d0d9e3; border-radius: 6px; font-size: 14px; font-family: inherit; text-align: center; }',
      '.cq-qty-input:focus { outline: none; border-color: #182e49; box-shadow: 0 0 0 3px rgba(24,46,73,.1); }',
      '.cq-edit-section { margin-top: 24px; padding-top: 20px; border-top: 1px solid #d0d9e3; }',
      '.cq-edit-label { font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #182e49; }',
      '.cq-edit-section textarea { width: 100%; padding: 10px 14px; border: 1.5px solid #d0d9e3; border-radius: 6px; font-size: 14px; font-family: inherit; margin-bottom: 10px; box-sizing: border-box; resize: vertical; }',
      '.cq-edit-section textarea:focus { outline: none; border-color: #182e49; box-shadow: 0 0 0 3px rgba(24,46,73,.1); }',
      // File input above the Request Changes button (2026-07-23) — same block/full-width/
      // margin-bottom treatment as the artwork upload's own file input just above this
      // section, so the two file pickers on this page look consistent.
      '.cq-edit-section input[type="file"] { display: block; width: 100%; font-size: 13px; margin-bottom: 12px; box-sizing: border-box; }',
      '.cq-artwork-upload input[type="file"] { display: block; width: 100%; font-size: 13px; margin-bottom: 12px; box-sizing: border-box; }',
      '.cq-artwork-upload textarea { width: 100%; padding: 10px 14px; border: 1.5px solid #d0d9e3; border-radius: 6px; font-size: 14px; font-family: inherit; box-sizing: border-box; resize: vertical; }',
      '.cq-artwork-upload textarea:focus { outline: none; border-color: #182e49; box-shadow: 0 0 0 3px rgba(24,46,73,.1); }',

      /* Mobile */
      '@media (max-width: 600px) {',
      '  .cq-row { flex-direction: column; gap: 0; }',
      '  .cq-pg-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }',
      // The 100px/18px sizing reads fine on a full-width desktop card but crowds a
      // phone-width one — back off toward the original smaller sizing on narrow screens.
      '  .cq-design-thumb { width: 72px; height: 72px; }',
      '  .cq-design-title { font-size: 15px; }',
      '  .cq-heading { font-size: 22px; }',
      '  .cq-modal-backdrop { padding: 12px; }',
      '  .cq-modal { max-height: 95vh; }',
      '  .cq-modal-content { padding: 16px 18px 18px; }',
      '  .cq-modal-spec-key { width: 90px; }',
      '  .cq-price-table { font-size: 12px; }',
      '  .cq-price-table th, .cq-price-table td { padding-top: 6px; padding-bottom: 6px; }',
      '  .cq-price-sku { display: block; font-size: 11px; }',
      '  .cq-price-qty { width: 12%; }',
      '  .cq-price-amount { width: 32%; padding-left: 8px !important; }',
      '  .cq-price-line-total, .cq-price-savings { font-size: 10px; }',
      '  .cq-price-table-wholesale { font-size: 11px; }',
      '  .cq-price-table-wholesale th { font-size: 9px; }',
      '  .cq-price-table-wholesale .cq-price-product { width: 34%; }',
      '  .cq-price-table-wholesale .cq-price-qty { width: 10%; padding-left: 2px !important; padding-right: 4px !important; }',
      '  .cq-price-table-wholesale .cq-price-amount, .cq-price-table-wholesale .cq-price-total { width: 28%; padding-left: 6px !important; }',
      '}',
      '@media (max-width: 380px) {',
      '  .cq-pg-grid { grid-template-columns: 1fr 1fr; }',
      '  .cq-cat-tabs { gap: 4px; }',
      '  .cq-cat-tab { padding: 5px 10px; font-size: 12px; }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

})();
